/**
 * DHTMLX task/link memos, column templates, parse recovery, and chart events for /schedule/tasks.
 * Route: /schedule/tasks.
 * Does not own save/discard, the toolbar, or the confirm dialogs.
 *
 * Do not put `$target` on task clones. DHTMLX fills `$target` during parse.
 * Hierarchy recovery uses clearAll + parse from store-derived parents.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ITaskStore } from '../../../stores/ITaskStore';
import { markGanttPerf, measureGanttPerf, logAutoscheduleDirtyRatio } from '../../../gantt/ganttPerfGuards';
import { getDhtmlxParentId, computeDurationFromStartAndFinish, taskHasChildren } from '../../../gantt/addTaskUtils';
import { ENABLE_TARGETS_QUANTITY_PANEL } from '../ganttPageFlags';
import { normalizeTaskSearchQuery } from '../../../gantt/taskNameSearch';
import { notifications } from '@mantine/notifications';
import { PERMISSION_RESTRICTED_MESSAGE } from '../../../permissions/scheduler';
import type { TaskSnapshot, LinkSnapshot } from '../../../stores/types';
import { useTaskTargetsData, capLookAheadExportEndDate, useGanttTargetsVisibleRows } from '../components/targets';
import { buildGanttColumns } from '../../../gantt/columns';
import { sessionColumnWidthMap, applyFixedColumnWidths, FLEX_GANTT_COLUMN_NAME, sumVisibleColumnWidths, chooseGridColumnLayoutMode, hasPinnedGanttColumnWidths, isPersistableGanttColumnName, saveGanttColumnWidths } from '../../../gantt/ganttColumnWidths';
import { applyScheduleGridColumnLayout, fitGanttColumnsToContent, disableGanttGridContentScroll, rememberScheduleGridWidth } from '../../../gantt/fitGanttColumnsToContent';
import { resolveScheduleGridWidth, saveGanttGridSplit, gridSharePercentFromWidths } from '../../../gantt/ganttGridSplit';
import { ganttDayScaleCellClasses, GanttWorkTimeApi } from '../../../gantt/ganttNonWorkingScaleDay';
import { isTodayScaleCell } from '../../../gantt/ganttTodayMarker';
import { formatGanttDateLabel, sameCalendarDay, applyCalendarDayKeepingTime } from '../../../gantt/ganttDateFormat';
import { resolveTaskName, formatDateForBackend, normalizeTaskType, unwrapPlanningFetchTaskResponse, planningFetchTaskRowId, mapPlanningApiTaskRowToSnapshot } from '../../../utils/utils';
import { effectiveUom } from '../../../gantt/effectiveUom';
import { syncPickerActivePaint } from '../../../gantt/ganttPickerActivePaint';
import { syncVisiblePendingRowClasses } from '../../../gantt/ganttPendingHighlightPaint';
import { useGanttLeaveGuard } from '../../../hooks/useGanttLeaveGuard';
import { perfMonitor } from '../../../utils/performanceMarkers';
import { columnKeysFromTaskPatch } from '../../../gantt/editedFieldKeys';
import { getEffectiveLocationIdForDisplay } from '../../../location-hierarchy/locationScopeUtils';
import { updateTaskMeta, convertTaskToWBS, fetchTask, exportProjectFile, exportLookAheadExcel, exportLookAheadPdf, exportScheduleExcel } from '../../../api/schedulingApis';
import { logPerfSummary } from '../../../stores';
import { exclusiveEndToInclusiveFinishWallClock, buildDhtmlxScheduleFields } from '../../../gantt/mspInclusiveEndBridge';
import { validatePredecessorLink } from '../../../gantt/predecessorValidation';
import { repopulateGanttLinkPointers, mergeLinkSnapshotsFromGantt } from '../../../gantt/taskLinkDisplayUtils';
import { installGanttTypedDateEditor, resolveDateEditorSeedDay, applyInclusiveDayToDateEditor } from '../../../gantt/ganttDateInlineEditor';
import { buildAssignTargetChipsFromTaskIds } from '../components/assignments/assignDisplayUtils';
import { buildSchedulePatchFromGanttTask } from '../../../gantt/scheduleFieldDiff';
import { linkGraphsEquivalent } from '../../../gantt/ganttLinkIndex';
import type { GanttToolsActionItem } from '../components/tools/GanttToolsActionList';
import { IconUserPlus, IconMapPin, IconTags, IconRefresh, IconFileTypeXml, IconFileSpreadsheet, IconFileTypePdf } from '@tabler/icons-react';
import { resolveGanttBusyOverlayState } from '../components/GanttBusyOverlay';

export function useGanttEngine(p: Record<string, any>) {
  const {
    viewMode,
    ganttTasksEpoch,
    collapsedBranchIdsRef,
    dhtmlxTasks,
    baselinesByTaskId,
    GANTT_BAR_HEIGHT,
    BASELINE_SUBROW_HEIGHT,
    GANTT_ROW_HEIGHT,
    ganttRef,
    collapsedBranchIds,
    zoomLevel,
    targetsShowFullWbs,
    targetsFocusTaskIds,
    targetsFocusExpandKeyRef,
    targetsWindow,
    setCollapsedBranchIds,
    showCriticalPathOnly,
    criticalFocusTaskIds,
    criticalFocusExpandKeyRef,
    criticalFilterEpoch,
    searchFocusTaskIds,
    searchFocusExpandKeyRef,
    debouncedTaskSearch,
    columnFilterFocusTaskIds,
    columnFilterExpandKeyRef,
    debouncedColumnFilterValues,
    taskNameSearchResult,
    setTaskSearchExpanded,
    taskSearchInputRef,
    calendarReady,
    canTaskCreateRef,
    openAddTaskModalRef,
    openTaskDetailsPanelRef,
    lookAheadLeafTaskIds,
    targetsColumns,
    targetsGateRef,
    links,
    compareBaselinesEnabled,
    compareBaselineIndex,
    dhtmlxBaselines,
    accumulatedChanges,
    visibleOptionalColumns,
    trackedLeafTaskIds,
    parentIdsWithChildren,
    canTaskCreate,
    canTaskDelete,
    canTaskEdit,
    predSuccLinkIndex,
    columnWidthsVersion,
    projectId,
    liveAssignments,
    autoScheduleEnabled,
    showGanttChart,
    BASELINE_BAR_HEIGHT,
    columnResizeInProgressRef,
    columnWidthsRef,
    ganttSkinTheme,
    setViewSwitchBusy,
    locationModalTaskIdRef,
    pendingChangeTaskIdsRef,
    criticalHighlightPaintRef,
    selectedAssignTaskIdsRef,
    escapeHtml,
    locationModalTaskId,
    predecessorPickerTaskId,
    pendingChangeTaskIds,
    pendingEditedFields,
    skipPendingHighlightRefreshRef,
    recalcEngine,
    canMutateTasks,
    scheduleRecalcLoading,
    saving,
    discarding,
    handleServerScheduleRecalculate,
    handleDhtmlxManualRecalculate,
    sessionMetadata,
    readinessDataReady,
    featureTips,
    addTaskModalOpen,
    isPanelOpen,
    locationModalOpen,
    deleteTaskTarget,
    saveConfirmOpen,
    discardConfirmOpen,
    reassignConfirmOpen,
    bulkUnknownTypeConfirmOpen,
    baselineModalOpen,
    liveScheduleOffModalOpen,
    targetsDiscardPending,
    addTaskSubmitting,
    closeAddTaskModal,
    setLocationModalOpen,
    setLocationModalTaskId,
    taskMetaSyncInProgressRef,
    patchGanttTaskFields,
    buildTaskMetaItem,
    getLocationNameById,
    syncTaskMetaInBackground,
    syncInheritedLocationDisplay,
    autoScheduleApplyingRef,
    beginLocalProcessing,
    scheduleBackgroundPersist,
    endLocalProcessing,
    dumpTimer,
    isDragging,
    endDateEditorCommitRef,
    lastLinkPointersRevisionRef,
    linksRevision,
    taskNameInlineEditAllowedRef,
    openLocationModalRef,
    closeLocationInlineEditor,
    predecessorSrNoCodeOverrideRef,
    setColumnWidthsVersion,
    dismissGanttInlineEditing,
    openPredecessorPickerRef,
    closePredecessorPicker,
    setScrollToLinkedIssuesRequest,
    selectedTaskId,
    selectVisibleLeavesForAssign,
    deselectVisibleLeavesForAssign,
    toggleAssignTaskSelected,
    assignLocationOptions,
    assignTaskCatalog,
    openAssignUsersDrawer,
    assignMockEnabled,
    openAddTaskModal,
    setDeleteTaskTarget,
    effectiveLocationIdByTaskId,
    predecessorLinkCommitInProgressRef,
    commitPredecessorLinkChanges,
    autoScheduleMuteSafetyTimerRef,
    highlightCriticalPath,
    setCriticalFilterEpoch,
    resolvePredecessorWbsCodeRef,
    setTrackTaskLoadingTaskId,
    setExporting,
    setExportProgress,
    setExportingExcel,
    reassignLocationsLoading,
    requestReassignLocations,
    bulkUnknownTypeLoading,
    requestBulkUnknownTaskTypes,
    handleInvalidateAndFetch,
    exporting,
    exportingExcel,
    baselineSubmitting,
    lookAheadExportWindowCaption,
    manualRecalculateLoading,
    viewSwitchBusy,
    pendingEditedFieldsRef,
    effectiveLocationIdByTaskIdRef,
    locationIdToNameRef,
    predecessorPickerTaskIdRef,
    storeLoading,
    loadingProgress,
    projectDisplayName,
    localProcessing,
  } = p;
  const store = p.store as ITaskStore;

/**
 * Baseline compare only applies in Schedule — keep this boolean stable across
 * Schedule↔Targets toggles when compare is off so we do NOT reclone ~15k tasks
 * (ReactGantt deep-diffs the whole array and that alone can take seconds).
 */
const compareBaselinesActive = compareBaselinesEnabled && viewMode === 'schedule';

/**
 * Always pass the FULL tree to DHTMLX (Schedule and Targets).
 * Look-ahead scoping uses the `filter` prop (hide in UI only) — do not omit
 * tasks from this array (that reparents nodes and corrupts hierarchy).
 *
 * Parent links are recomputed from TaskSnapshot (not trusted from dhtmlx cache),
 * because DHTMLX mutates task objects in place and can poison cached parents.
 *
 * Collapse state is intentionally omitted from deps: individual open/close is
 * handled by DHTMLX; expand-all uses `applyTreeOpenState`. Reading collapse from
 * a ref keeps remount/parse correct without recloning ~15k rows on every toggle.
 *
 * Do NOT add $target on these clones. DHTMLX populates task.$target from links during parse
 * and onAfterLinkAdd. If we add $target, React Gantt's diff sees 20k "updated" tasks,
 * triggers incremental updates with initTask() which wipes $source/$target, and only
 * the 2 tasks touched by link add/delete get repopulated - leaving the rest blank.
 *
 * Baselines: pass Date objects via BOTH the `baselines` prop and task.baselines.
 * The renderer reads task.baselines; the prop feeds the datastore. Use Dates only —
 * strings on task.baselines skip parseDate and cause "Invalid day index".
 */
const tasks = useMemo(() => {
  markGanttPerf('reactClone');
  const snapshots = store.getTaskValues();
  const taskById = new Map(snapshots.map((t) => [t.id, t]));
  // ganttTasksEpoch forces a new array identity after view/zoom recovery.
  void ganttTasksEpoch;
  const collapsed = collapsedBranchIdsRef.current;

  const cloned = dhtmlxTasks.map((task: any) => {
    const id = String(task.id);
    const open = !collapsed.has(id);
    const taskBaselines = baselinesByTaskId.get(id);
    const compareRowHeight = GANTT_BAR_HEIGHT + BASELINE_SUBROW_HEIGHT;
    const snap = taskById.get(id) ?? taskById.get(String(task.id));
    const parent = snap ? getDhtmlxParentId(snap, taskById) : task.parent;

    // Explicit field copy so DHTMLX cannot mutate the cached dhtmlxTasks objects.
    const clone: Record<string, unknown> = {
      id: task.id,
      text: task.text,
      parent,
      start_date: task.start_date,
      end_date: task.end_date,
      duration: task.duration,
      actualStart: task.actualStart,
      actualFinish: task.actualFinish,
      actualDuration: task.actualDuration,
      progress: task.progress,
      type: task.type,
      taskType: task.taskType,
      locationType: task.locationType,
      locationId: task.locationId,
      ...(task.calendar_id ? { calendar_id: task.calendar_id } : {}),
      auto_scheduling: task.auto_scheduling,
      readonly: task.readonly,
      open,
      bar_height: GANTT_BAR_HEIGHT,
      row_height: compareBaselinesActive ? compareRowHeight : GANTT_ROW_HEIGHT,
      baselines: compareBaselinesActive ? (taskBaselines ?? []) : [],
    };
    return clone;
  });
  measureGanttPerf('reactClone');
  return cloned;
}, [
  dhtmlxTasks,
  baselinesByTaskId,
  compareBaselinesActive,
  ganttTasksEpoch,
  store,
]);

/** Live DHTMLX tree — source of truth for row order + leaf/non-leaf (quantity panel only). */
const targetsVisibleRows = useGanttTargetsVisibleRows(
  ganttRef,
  ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets',
  `${tasks.length}:${collapsedBranchIds.size}:${ganttTasksEpoch}:${zoomLevel}:${targetsShowFullWbs}:${targetsFocusTaskIds?.size ?? 'all'}`,
);

/**
 * When entering look-ahead focus (or window changes), expand focus ancestors so
 * leaves are not hidden under collapsed parents. Does not run on every toggle
 * of a single branch.
 */
useEffect(() => {
  if (viewMode !== 'targets' || targetsShowFullWbs || !targetsFocusTaskIds) {
    targetsFocusExpandKeyRef.current = '';
    return;
  }
  const key = `${targetsWindow.start.getTime()}:${targetsWindow.end.getTime()}:${targetsFocusTaskIds.size}`;
  if (targetsFocusExpandKeyRef.current === key) return;
  targetsFocusExpandKeyRef.current = key;
  setCollapsedBranchIds((prev: Set<string>) => {
    let changed = false;
    const next = new Set(prev);
    for (const id of targetsFocusTaskIds) {
      if (next.delete(id)) changed = true;
    }
    return changed ? next : prev;
  });
}, [
  viewMode,
  targetsShowFullWbs,
  targetsFocusTaskIds,
  targetsWindow.start,
  targetsWindow.end,
]);

/** Expand ancestors when Critical path only turns on / set changes. */
useEffect(() => {
  if (!showCriticalPathOnly || !criticalFocusTaskIds) {
    criticalFocusExpandKeyRef.current = '';
    return;
  }
  const key = `cp:${criticalFilterEpoch}:${criticalFocusTaskIds.size}`;
  if (criticalFocusExpandKeyRef.current === key) return;
  criticalFocusExpandKeyRef.current = key;
  setCollapsedBranchIds((prev: Set<string>) => {
    let changed = false;
    const next = new Set(prev);
    for (const id of criticalFocusTaskIds) {
      if (next.delete(id)) changed = true;
    }
    return changed ? next : prev;
  });
}, [showCriticalPathOnly, criticalFocusTaskIds, criticalFilterEpoch]);

/** Expand ancestors for task-name search hits so matches are not under collapsed parents. */
useEffect(() => {
  if (!searchFocusTaskIds || searchFocusTaskIds.size === 0) {
    searchFocusExpandKeyRef.current = '';
    return;
  }
  const key = `search:${normalizeTaskSearchQuery(debouncedTaskSearch)}:${searchFocusTaskIds.size}`;
  if (searchFocusExpandKeyRef.current === key) return;
  searchFocusExpandKeyRef.current = key;
  setCollapsedBranchIds((prev: Set<string>) => {
    let changed = false;
    const next = new Set(prev);
    for (const id of searchFocusTaskIds) {
      if (next.delete(id)) changed = true;
    }
    return changed ? next : prev;
  });
}, [searchFocusTaskIds, debouncedTaskSearch]);

/** Expand ancestors for multi-column filter hits. */
useEffect(() => {
  if (!columnFilterFocusTaskIds || columnFilterFocusTaskIds.size === 0) {
    columnFilterExpandKeyRef.current = '';
    return;
  }
  const key = `col:${columnFilterFocusTaskIds.size}:${JSON.stringify(debouncedColumnFilterValues)}`;
  if (columnFilterExpandKeyRef.current === key) return;
  columnFilterExpandKeyRef.current = key;
  setCollapsedBranchIds((prev: Set<string>) => {
    let changed = false;
    const next = new Set(prev);
    for (const id of columnFilterFocusTaskIds) {
      if (next.delete(id)) changed = true;
    }
    return changed ? next : prev;
  });
}, [columnFilterFocusTaskIds, debouncedColumnFilterValues]);

/** Scroll the first name match into view after the display filter updates. */
useEffect(() => {
  const firstId = taskNameSearchResult?.firstMatchId;
  if (!firstId) return;
  const gantt = ganttRef.current?.instance as {
    isTaskExists?: (id: string | number) => boolean;
    showTask?: (id: string | number) => void;
  } | undefined;
  const raf = window.requestAnimationFrame(() => {
    try {
      if (gantt?.isTaskExists?.(firstId)) {
        gantt.showTask?.(firstId);
      }
    } catch {
      // ignore
    }
  });
  return () => window.cancelAnimationFrame(raf);
}, [taskNameSearchResult?.firstMatchId, debouncedTaskSearch]);

/** ⌘/Ctrl+F expands and focuses the toolbar task search (override browser Find on this page). */
useEffect(() => {
  const onKeyDown = (e: KeyboardEvent) => {
    if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'f') return;
    e.preventDefault();
    setTaskSearchExpanded(true);
    window.requestAnimationFrame(() => {
      const input = taskSearchInputRef.current;
      if (!input) return;
      input.focus();
      input.select();
    });
  };
  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}, []);

/**
 * Keyboard navigation: neutralize ctrl+z / ctrl+r until undo is wired to Zustand,
 * map Enter → our task details, Ctrl+Enter → Add Task modal (not DHTMLX createTask),
 * and focus the gantt root.
 */
useEffect(() => {
  if (!calendarReady) return;
  const gantt = ganttRef.current?.instance as {
    removeShortcut?: (shortcut: string, scope: string) => void;
    addShortcut?: (
      shortcut: string,
      handler: (this: { taskId?: string | number }) => boolean | void,
      scope?: string,
    ) => void;
    attachEvent?: (name: string, handler: (...args: any[]) => unknown) => string | number;
    detachEvent?: (id: string | number) => void;
    isTaskExists?: (id: string | number) => boolean;
    $root?: HTMLElement;
    $container?: HTMLElement;
  } | undefined;
  if (!gantt) return;

  const notifyCreateDenied = () => {
    notifications.show({
      title: 'Permission required',
      message: PERMISSION_RESTRICTED_MESSAGE,
      color: 'orange',
    });
  };

  const openAddUnderFocusedTask = (parentId: string | number | null | undefined) => {
    if (!canTaskCreateRef.current) {
      notifyCreateDenied();
      return;
    }
    if (parentId == null || parentId === '') {
      openAddTaskModalRef.current(null);
      return;
    }
    const id = String(parentId);
    if (gantt.isTaskExists && !gantt.isTaskExists(id)) return;
    openAddTaskModalRef.current(id);
  };

  let beforeLightboxId: string | number | null = null;
  let beforeTaskAddId: string | number | null = null;
  try {
    gantt.removeShortcut?.('ctrl+z', 'gantt');
    gantt.removeShortcut?.('ctrl+r', 'gantt');
    gantt.addShortcut?.(
      'ctrl+z',
      () => {
        /* Undo deferred — avoid calling missing gantt.undo */
        return false;
      },
      'gantt',
    );
    gantt.addShortcut?.(
      'ctrl+r',
      () => {
        /* Redo deferred */
        return false;
      },
      'gantt',
    );

    // Default Enter opens DHTMLX lightbox — route to our task details panel instead.
    gantt.addShortcut?.(
      'enter',
      function enterOpensAppTaskDetails(this: { taskId?: string | number }) {
        const id = this?.taskId;
        if (id == null || id === '') return false;
        openTaskDetailsPanelRef.current(id);
        return false;
      },
      'taskRow',
    );

    // Default Ctrl+Enter calls gantt.createTask ("New task") outside our store/save flow.
    gantt.addShortcut?.(
      'ctrl+enter',
      function ctrlEnterOpensAddTaskModal(this: { taskId?: string | number }) {
        openAddUnderFocusedTask(this?.taskId);
        return false;
      },
      'taskRow',
    );
    gantt.addShortcut?.(
      'ctrl+enter',
      function ctrlEnterFromHeaderOpensAddRoot() {
        openAddUnderFocusedTask(null);
        return false;
      },
      'headerCell',
    );

    if (typeof gantt.attachEvent === 'function') {
      beforeLightboxId = gantt.attachEvent('onBeforeLightbox', (id: string | number) => {
        if (id != null && id !== '') {
          openTaskDetailsPanelRef.current(id);
        }
        return false;
      });
      // Block any leftover DHTMLX inline creates so orphan "New task" rows never appear.
      beforeTaskAddId = gantt.attachEvent('onBeforeTaskAdd', () => false);
    }
  } catch {
    // ignore if shortcut / lightbox API unavailable
  }
  const root = gantt.$root ?? gantt.$container;
  if (root && !root.hasAttribute('tabindex')) {
    root.setAttribute('tabindex', '0');
  }

  return () => {
    if (typeof gantt.detachEvent === 'function') {
      try {
        if (beforeLightboxId != null) gantt.detachEvent(beforeLightboxId);
        if (beforeTaskAddId != null) gantt.detachEvent(beforeTaskAddId);
      } catch {
        // ignore
      }
    }
  };
}, [calendarReady]);

const lookAheadSnapshots = useMemo(() => {
  if (
    !ENABLE_TARGETS_QUANTITY_PANEL ||
    viewMode !== 'targets' ||
    !lookAheadLeafTaskIds
  ) {
    return [] as TaskSnapshot[];
  }
  return store.getTasks().filter((t) => lookAheadLeafTaskIds.has(String(t.id)));
}, [viewMode, store, lookAheadLeafTaskIds, dhtmlxTasks]);

const targetsData = useTaskTargetsData({
  projectId,
  enabled: ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets',
  columns: targetsColumns,
  frequency: targetsWindow.frequency,
  tasks: lookAheadSnapshots,
});
targetsGateRef.current = {
  dirtyCount: targetsData.dirtyCount,
  discardDrafts: targetsData.discardDrafts,
};

/**
 * DHTMLX tree branches can stay corrupt after filtered parses / layout toggles.
 * ReactGantt's incremental updateTask does NOT rebuild parent branches when parent
 * values match by deep-equal — so bumping clones alone is not enough.
 * clearAll + parse from store-derived parents restores hierarchy after view/zoom.
 */
const ganttTasksForParseRef = useRef(tasks);
const ganttLinksForParseRef = useRef(links);
ganttTasksForParseRef.current = tasks;
ganttLinksForParseRef.current = links;

useEffect(() => {
  if (!calendarReady || ganttTasksEpoch === 0) return;
  const gantt = ganttRef.current?.instance as {
    clearAll?: () => void;
    parse?: (data: { data: unknown[]; links: unknown[] }) => void;
    batchUpdate?: (cb: () => void) => void;
    eachTask?: (cb: (task: { id: string | number; $open?: boolean }) => void) => void;
    hasChild?: (id: string | number) => boolean;
    render?: () => void;
  } | undefined;
  if (!gantt?.clearAll || !gantt?.parse) return;

  const data = ganttTasksForParseRef.current;
  const linkData = ganttLinksForParseRef.current;
  if (!data.length) return;

  const collapsed = collapsedBranchIdsRef.current;
  const rebuild = () => {
    gantt.clearAll?.();
    gantt.parse?.({ data, links: linkData });
    // Parse may not honor `open` for every build — restore collapse state.
    gantt.eachTask?.((task) => {
      if (gantt.hasChild?.(task.id)) {
        task.$open = !collapsed.has(String(task.id));
      }
    });
  };
  if (gantt.batchUpdate) gantt.batchUpdate(rebuild);
  else {
    rebuild();
    gantt.render?.();
  }
}, [ganttTasksEpoch, calendarReady]);



// Keep live gantt task heights / baselines in sync after Compare toggles.
useEffect(() => {
  const gantt = ganttRef.current?.instance as {
    eachTask?: (
      cb: (task: {
        id?: string | number;
        baselines?: unknown[];
        bar_height?: number;
        row_height?: number;
      }) => void,
    ) => void;
    getTaskBaselines?: (id: string | number) => unknown[];
    adjustTaskHeightForBaselines?: (task: unknown) => void;
    render?: () => void;
  } | undefined;
  if (!gantt?.render) return;
  const timer = window.setTimeout(() => {
    if (gantt.eachTask) {
      gantt.eachTask((task) => {
        try {
          if (compareBaselinesEnabled && gantt.getTaskBaselines) {
            const fromStore = gantt.getTaskBaselines(task.id as string | number);
            task.baselines = fromStore?.length ? (fromStore as unknown[]) : [];
          } else {
            task.baselines = [];
          }
          task.bar_height = GANTT_BAR_HEIGHT;
          // Match tasks useMemo: compare mode uses a fixed taller row for all tasks.
          task.row_height = compareBaselinesEnabled
            ? GANTT_BAR_HEIGHT + BASELINE_SUBROW_HEIGHT
            : GANTT_ROW_HEIGHT;
          if (compareBaselinesEnabled && task.baselines?.length) {
            gantt.adjustTaskHeightForBaselines?.(task);
            // Re-assert equal height so empty-baseline rows stay aligned.
            task.row_height = GANTT_BAR_HEIGHT + BASELINE_SUBROW_HEIGHT;
          }
        } catch {
          // ignore per-task sync failures
        }
      });
    }
    gantt.render?.();
  }, 0);
  return () => window.clearTimeout(timer);
}, [compareBaselinesEnabled, compareBaselineIndex, dhtmlxBaselines]);

/**
 * Predecessor: single-click uses DHTMLX predecessor editor; pick-button opens
 * PredecessorTaskPickerPanel. Task details / Add Task still use Pick → picker.
 */

// Columns are memoized separately so Schedule↔Targets (viewMode) does not replace
// the columns array and wipe user-resized widths / break resize handles.
// Assignees are read from a ref, same as the location / predecessor picker ids,
// so opening Assign users does not rebuild columns and re-render every task.
const liveAssignmentsRef = useRef(liveAssignments);
liveAssignmentsRef.current = liveAssignments;
const ganttColumns = useMemo(
  () =>
    buildGanttColumns({
      projectId,
      store,
      columnWidthsRef,
      visibleOptionalColumns,
      selectedAssignTaskIdsRef,
      pendingEditedFieldsRef,
      accumulatedChangesRef: accumulatedChanges,
      ganttRef: ganttRef as any,
      parentIdsWithChildren,
      canTaskCreate,
      canTaskDelete,
      effectiveLocationIdByTaskIdRef,
      locationIdToNameRef,
      locationModalTaskIdRef,
      predecessorPickerTaskIdRef,
      predSuccLinkIndex,
      trackedLeafTaskIds,
      liveAssignmentsRef,
      compareBaselinesEnabled,
      compareBaselineIndex,
    }),
  [
    store,
    visibleOptionalColumns,
    trackedLeafTaskIds,
    parentIdsWithChildren,
    compareBaselinesEnabled,
    compareBaselineIndex,
    canTaskCreate,
    canTaskDelete,
    canTaskEdit,
    predSuccLinkIndex,
    columnWidthsVersion,
    projectId,
  ],
);

/** Repaint visible assignee cells when the payload changes. Do not gantt.render. */
const seenLiveAssignmentsRef = useRef(liveAssignments);
useEffect(() => {
  if (seenLiveAssignmentsRef.current === liveAssignments) return;
  if (!calendarReady) return;
  seenLiveAssignmentsRef.current = liveAssignments;
  if (!visibleOptionalColumns.assignees) return;
  const gantt = ganttRef.current?.instance as {
    isTaskExists?: (id: string) => boolean;
    refreshTask?: (id: string) => void;
    $container?: HTMLElement;
  } | undefined;
  const root = gantt?.$container;
  if (!gantt?.refreshTask || !root) return;
  const ids = new Set<string>();
  root.querySelectorAll('.gantt-assignees-cell').forEach((node) => {
    const id = node.getAttribute('data-task-id');
    if (id) ids.add(id);
  });
  for (const id of ids) {
    if (gantt.isTaskExists?.(id)) gantt.refreshTask(id);
  }
}, [liveAssignments, calendarReady, visibleOptionalColumns.assignees]);

/**
 * Gantt config
 */
const config = useMemo(() => ({
  readonly: !canTaskEdit && !canTaskCreate && !canTaskDelete,
  show_errors: true,
  details_on_dblclick: false,
  duration_unit: 'day',
  date_format: '%Y-%m-%d %H:%i',
  /** Display format for grid inline date editors (dhtmlx tokens). */
  date_grid: '%D %d/%M/%y',
  resize_columns: true,
  /**
   * Schedule / Look ahead: fill the grid panel when columns fit; overflow enables
   * grid H-scroll via applyScheduleGridColumnLayout. Quantity panel uses content-fit.
   */
  autofit: !(ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets'),
  keep_grid_width: false,
  grid_elastic_columns: !(ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets'),
  drag_project: true,
  highlight_critical_path: true,  // Highlight critical path tasks and links
  auto_scheduling: autoScheduleEnabled,
  auto_scheduling_initial: false,
  auto_scheduling_strict: false,
  auto_scheduling_move_projects: true,
  auto_scheduling_use_progress: true,  // Exclude completed tasks from critical path
  static_background: true,
  show_task_cells: false,
  smart_scales: true,
  show_progress: true,
  show_markers: true,
  smart_rendering: true,
  /** Row focus / arrow keys; cell mode stays off so inline editors keep working. */
  keyboard_navigation: true,
  keyboard_navigation_cells: false,
  /**
   * Timeline chart visibility. Quantity panel mode always hides the chart.
   * User toggle (View → Gantt chart) otherwise controls show_chart.
   */
  show_chart:
    showGanttChart && !(ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets'),
  work_time: true,
  row_height: GANTT_ROW_HEIGHT,
  bar_height: GANTT_BAR_HEIGHT,
  baselines: {
    datastore: 'baselines',
    render_mode: compareBaselinesEnabled ? 'separateRow' : false,
    dataprocessor_baselines: false,
    row_height: BASELINE_SUBROW_HEIGHT,
    bar_height: BASELINE_BAR_HEIGHT,
  },
  scales: [
    { unit: 'year', step: 1, format: '%Y' },
    { unit: 'month', step: 1, format: '%M' },
    { unit: 'day', step: 1, format: '%DD' },
  ],
  columns: ganttColumns,
}), [
  ganttColumns,
  viewMode,
  autoScheduleEnabled,
  compareBaselinesEnabled,
  canTaskCreate,
  canTaskDelete,
  canTaskEdit,
  showGanttChart,
]);

/**
 * Re-apply session column widths on every paint (fill vs scroll).
 * Skip while a column resize drag is in progress so the gesture stays smooth.
 */
useEffect(() => {
  if (!calendarReady) return;
  let handlerId: number | string | null = null;
  let tries = 0;
  const timer = window.setInterval(() => {
    tries += 1;
    const gantt = ganttRef.current?.instance as {
      attachEvent?: (name: string, cb: () => boolean) => number | string;
      detachEvent?: (id: number | string) => void;
      getGridColumns?: () => Array<{ name?: string; width?: number; hide?: boolean }>;
      config?: {
        columns?: Array<{ name?: string; width?: number; hide?: boolean }>;
        autofit?: boolean;
        grid_elastic_columns?: boolean;
        grid_width?: number;
      };
      $ui?: {
        getView?: (
          id: string,
        ) => { $config?: { scrollable?: boolean; width?: number } } | undefined;
      };
    } | null;
    if (!gantt?.attachEvent) {
      if (tries > 40) window.clearInterval(timer);
      return;
    }
    window.clearInterval(timer);
    handlerId = gantt.attachEvent('onBeforeGanttRender', () => {
      if (columnResizeInProgressRef.current) return true;
      const cols = gantt.getGridColumns?.() ?? gantt.config?.columns;
      if (!cols?.length) return true;
      const widthMap = sessionColumnWidthMap(columnWidthsRef.current);
      const panelWidth =
        (typeof gantt.config?.grid_width === 'number' && gantt.config.grid_width > 0
          ? gantt.config.grid_width
          : gantt.$ui?.getView?.('grid')?.$config?.width) ?? 0;
      applyFixedColumnWidths(cols, widthMap, {
        flexColumn: FLEX_GANTT_COLUMN_NAME,
        mode: 'fill',
      });
      const sumForMode = sumVisibleColumnWidths(
        cols.map((c) =>
          c.name === FLEX_GANTT_COLUMN_NAME &&
          typeof widthMap[FLEX_GANTT_COLUMN_NAME] === 'number'
            ? { ...c, width: widthMap[FLEX_GANTT_COLUMN_NAME] }
            : c,
        ),
        widthMap,
      );
      const mode = chooseGridColumnLayoutMode(sumForMode, panelWidth);
      applyFixedColumnWidths(cols, widthMap, {
        flexColumn: FLEX_GANTT_COLUMN_NAME,
        mode,
      });
      if (gantt.config) {
        if (mode === 'scroll') {
          gantt.config.autofit = false;
          gantt.config.grid_elastic_columns = false;
        } else {
          gantt.config.autofit = true;
          gantt.config.grid_elastic_columns = true;
        }
      }
      const grid = gantt.$ui?.getView?.('grid');
      if (grid?.$config) {
        grid.$config.scrollable = mode === 'scroll';
      }
      return true;
    });
  }, 50);

  return () => {
    window.clearInterval(timer);
    const g = ganttRef.current?.instance as {
      detachEvent?: (id: number | string) => void;
    } | null;
    if (handlerId != null && g?.detachEvent) g.detachEvent(handlerId);
  };
}, [calendarReady, projectId, columnWidthsVersion]);

/**
 * Quantity panel: content-sized columns + grid H-scroll.
 * Schedule / Look ahead: session widths + fill vs scroll; drag grid↔timeline divider.
 * Also re-assert skin after show_chart toggles so light/dark bar colors stay stable.
 */
useEffect(() => {
  const gantt = ganttRef.current?.instance as {
    setSkin?: (skin: string) => void;
    skin?: string;
  } | null;
  if (!gantt || !calendarReady) return;

  let cancelled = false;
  const apply = () => {
    if (cancelled) return;
    if (gantt.setSkin && gantt.skin !== ganttSkinTheme) {
      gantt.setSkin(ganttSkinTheme);
    }
    let restoreTimer: number | undefined;
    const restoreSessionColumnWidths = () => {
      if (columnResizeInProgressRef.current) return;
      const rootW =
        (gantt as { $root?: { clientWidth?: number } }).$root?.clientWidth ?? 0;
      applyScheduleGridColumnLayout(
        gantt as Parameters<typeof applyScheduleGridColumnLayout>[0],
        {
          liveWidths: columnWidthsRef.current,
          preferredGridWidth: !showGanttChart
            ? rootW > 0
              ? rootW
              : resolveScheduleGridWidth(rootW, projectId)
            : resolveScheduleGridWidth(rootW, projectId),
        },
      );
    };
    if (ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets') {
      fitGanttColumnsToContent(gantt as Parameters<typeof fitGanttColumnsToContent>[0]);
      // After content-fit, re-pin any session widths the user had set.
      const cols =
        (gantt as { getGridColumns?: () => Array<{ name?: string; width?: number; hide?: boolean }> })
          .getGridColumns?.() ??
        (gantt as { config?: { columns?: Array<{ name?: string; width?: number; hide?: boolean }> } })
          .config?.columns;
      if (cols?.length) {
        applyFixedColumnWidths(cols, sessionColumnWidthMap(columnWidthsRef.current), {
          flexColumn: FLEX_GANTT_COLUMN_NAME,
          mode: 'scroll',
        });
      }
    } else {
      const rootWidth =
        (gantt as { $root?: { clientWidth?: number } }).$root?.clientWidth ?? 0;
      const preferredGridWidth = !showGanttChart
        ? rootWidth > 0
          ? rootWidth
          : resolveScheduleGridWidth(rootWidth, projectId)
        : resolveScheduleGridWidth(rootWidth, projectId);
      disableGanttGridContentScroll(
        gantt as Parameters<typeof disableGanttGridContentScroll>[0],
        {
          preferredGridWidth,
          liveWidths: columnWidthsRef.current,
        },
      );
      // show_chart layout rebuild can run just after — re-assert Schedule split.
      restoreTimer = window.setTimeout(() => {
        if (cancelled) return;
        const rootW =
          (gantt as { $root?: { clientWidth?: number } }).$root?.clientWidth ?? 0;
        disableGanttGridContentScroll(
          gantt as Parameters<typeof disableGanttGridContentScroll>[0],
          {
            preferredGridWidth: !showGanttChart
              ? rootW > 0
                ? rootW
                : resolveScheduleGridWidth(rootW, projectId)
              : resolveScheduleGridWidth(rootW, projectId),
            liveWidths: columnWidthsRef.current,
          },
        );
        restoreSessionColumnWidths();
      }, 100);
    }
    // Keep overlay up through ReactGantt's debounced render after config/filter changes.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          if (!cancelled) setViewSwitchBusy(false);
        }, 120);
      });
    });
    return restoreTimer;
  };
  let restoreTimer: number | undefined;
  const raf = window.requestAnimationFrame(() => {
    restoreTimer = apply();
    const g = ganttRef.current?.instance as {
      getGridColumns?: () => Array<{ name?: string; width?: number; hide?: boolean }>;
      config?: {
        columns?: Array<{ name?: string; width?: number; hide?: boolean }>;
        autofit?: boolean;
        grid_elastic_columns?: boolean;
        grid_width?: number;
      };
      setSizes?: () => void;
      render?: () => void;
    } | null;
    if (g && !(ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets')) {
      if (!columnResizeInProgressRef.current) {
        const rootW =
          (g as { $root?: { clientWidth?: number } }).$root?.clientWidth ?? 0;
        applyScheduleGridColumnLayout(g as Parameters<typeof applyScheduleGridColumnLayout>[0], {
          liveWidths: columnWidthsRef.current,
          preferredGridWidth: !showGanttChart
            ? rootW > 0
              ? rootW
              : resolveScheduleGridWidth(rootW, projectId)
            : resolveScheduleGridWidth(rootW, projectId),
          skipRender: true,
        });
      }
    }
  });
  // Safety: never leave the overlay stuck if apply is skipped/raced.
  const safety = window.setTimeout(() => {
    if (!cancelled) setViewSwitchBusy(false);
  }, 8000);
  return () => {
    cancelled = true;
    window.cancelAnimationFrame(raf);
    if (restoreTimer != null) window.clearTimeout(restoreTimer);
    window.clearTimeout(safety);
  };
}, [viewMode, calendarReady, ganttColumns, ganttSkinTheme, projectId, showGanttChart]);
/**
 * Templates: grid_row_class highlights the row opened for location picker (cell background set in CSS),
 * marks non-leaf rows so schedule columns can look read-only, and paints light-yellow pending edits.
 * Timeline bar colors use dhtmlx built-in theme (terrace / dark) — not overridden here.
 */
const templates = useMemo(
  () => ({
    grid_row_class: (_start: Date, _end: Date, task: any) => {
      const classes: string[] = [];
      const id = String(task?.id ?? '');
      if (
        locationModalTaskIdRef.current != null &&
        id === String(locationModalTaskIdRef.current)
      ) {
        classes.push('gantt-row-location-picker-selected');
      }
      // Read pending from ref so cascade dirty sets do not recreate templates prop.
      if (pendingChangeTaskIdsRef.current.has(id)) {
        classes.push('gantt-row-pending-change');
      }
      const criticalPaint = criticalHighlightPaintRef.current;
      if (criticalPaint.ids.has(id)) {
        classes.push('gantt-row-critical-highlight');
        if (criticalPaint.overdue) classes.push('gantt-row-critical-highlight--overdue');
      }
      if (selectedAssignTaskIdsRef.current.has(id)) {
        classes.push('gantt-row-assign-selected');
      }
      const hasChildren = parentIdsWithChildren.has(id);
      const isNonLeaf =
        hasChildren || task?.type === 'project' || !!store.getTask(id)?.summary;
      if (isNonLeaf) {
        classes.push('gantt-row-non-leaf');
      }
      return classes.join(' ');
    },
    task_row_class: (_start: Date, _end: Date, task: any) => {
      const id = String(task?.id ?? '');
      const classes: string[] = [];
      if (pendingChangeTaskIdsRef.current.has(id)) {
        classes.push('gantt-row-pending-change');
      }
      const criticalPaint = criticalHighlightPaintRef.current;
      if (criticalPaint.ids.has(id)) {
        classes.push('gantt-row-critical-highlight');
        if (criticalPaint.overdue) classes.push('gantt-row-critical-highlight--overdue');
      }
      if (selectedAssignTaskIdsRef.current.has(id)) {
        classes.push('gantt-row-assign-selected');
      }
      return classes.join(' ');
    },
    scale_cell_class: (date: Date) =>
      ganttDayScaleCellClasses({
        date,
        zoomLevel,
        gantt: ganttRef.current?.instance as GanttWorkTimeApi | undefined,
        isToday: isTodayScaleCell(date, zoomLevel),
        kind: 'scale',
        // Shared template hits Month + Day rows; never grey here.
        applyNonWorking: false,
      }),
    timeline_cell_class: (_item: any, date: Date) =>
      ganttDayScaleCellClasses({
        date,
        zoomLevel,
        gantt: ganttRef.current?.instance as GanttWorkTimeApi | undefined,
        isToday: isTodayScaleCell(date, zoomLevel),
        kind: 'timeline',
        applyNonWorking: true,
      }),
    grid_date_format: (date: Date) => formatGanttDateLabel(date),
    task_date: (date: Date) => formatGanttDateLabel(date),
    task_end_date: (date: Date) => formatGanttDateLabel(date),
    tooltip_text: (start: Date, end: Date, task: any) => {
      const name = escapeHtml(resolveTaskName(task) || String(task?.text ?? ''));
      const startLabel = escapeHtml(formatGanttDateLabel(start));
      const endLabel = escapeHtml(formatGanttDateLabel(end));
      return (
        `<div>Task: ${name}</div>` +
        `<div>Start date: ${startLabel}</div>` +
        `<div>End date: ${endLabel}</div>`
      );
    },
  }),
  [parentIdsWithChildren, store, zoomLevel, calendarReady]
);

/** Paint Location / Predecessor picker active chrome without rebuilding ganttColumns. */
useEffect(() => {
  if (!calendarReady) return;
  const gantt = ganttRef.current?.instance as { $container?: HTMLElement } | undefined;
  const ganttRoot =
    gantt?.$container ??
    (document.querySelector('.fbt-gantt-root') as HTMLElement | null);
  if (!ganttRoot) return;
  syncPickerActivePaint(ganttRoot, locationModalTaskId, predecessorPickerTaskId);
}, [calendarReady, locationModalTaskId, predecessorPickerTaskId]);

/** Refresh only rows whose pending-edit highlight changed (avoid full gantt.render). */
const prevPendingHighlightRef = useRef<Set<string>>(new Set());
useEffect(() => {
  const gantt = ganttRef.current?.instance as {
    isTaskExists?: (id: string) => boolean;
    refreshTask?: (id: string) => void;
    $container?: HTMLElement;
  } | undefined;
  const ids = new Set<string>(pendingChangeTaskIds as Iterable<string>);
  for (const id of pendingEditedFields.keys()) ids.add(id);

  const ganttRoot =
    gantt?.$container ??
    (document.querySelector('.fbt-gantt-root') as HTMLElement | null);

  /** Strip / paint highlight on smart-rendered rows only — O(visible), not O(affected). */
  const syncVisiblePendingPaint = (pendingIds: Set<string>) => {
    if (!ganttRoot) return;
    syncVisiblePendingRowClasses(ganttRoot, pendingIds);
  };

  /** Cell wrappers need template refresh; keep that to edited fields (usually few). */
  const refreshEditedFieldCells = () => {
    if (!gantt?.refreshTask) return;
    for (const id of pendingEditedFields.keys()) {
      if (gantt.isTaskExists?.(id)) gantt.refreshTask(id);
    }
  };

  if (skipPendingHighlightRefreshRef.current) {
    skipPendingHighlightRefreshRef.current = false;
    // Cascade / manual recalc skip mass refreshTask — still paint what's on screen.
    syncVisiblePendingPaint(ids);
    if (ids.size > 0) refreshEditedFieldCells();
    prevPendingHighlightRef.current = ids;
    return;
  }

  if (!gantt?.refreshTask) {
    syncVisiblePendingPaint(ids);
    prevPendingHighlightRef.current = ids;
    return;
  }
  const prev = prevPendingHighlightRef.current;
  const toRefresh = new Set<string>();
  for (const id of ids) {
    if (!prev.has(id)) toRefresh.add(id);
  }
  for (const id of prev) {
    if (!ids.has(id)) toRefresh.add(id);
  }
  // Same task ids but field set may have grown — refresh those too.
  for (const id of pendingEditedFields.keys()) {
    if (prev.has(id)) toRefresh.add(id);
  }
  prevPendingHighlightRef.current = ids;

  // Cap: mass refreshTask on cascade-sized sets freezes the tab for minutes.
  // Sync CSS on mounted rows instead of refreshing thousands of task ids.
  if (toRefresh.size > 80) {
    syncVisiblePendingPaint(ids);
    refreshEditedFieldCells();
    return;
  }
  for (const id of toRefresh) {
    if (gantt.isTaskExists?.(id)) {
      gantt.refreshTask(id);
    }
  }
}, [pendingChangeTaskIds, pendingEditedFields]);


/** One-shot recalc while Live Schedule is off — preference picks server vs DHTMLX. */
const handleManualRecalculate = useCallback(() => {
  if (recalcEngine === 'MPXJ' || recalcEngine === 'CPM') {
    if (!canMutateTasks) {
      notifications.show({
        title: 'Permission required',
        message: 'You do not have permission to recalculate the schedule.',
        color: 'orange',
      });
      return;
    }
    if (!projectId || scheduleRecalcLoading || saving || discarding) return;
    void handleServerScheduleRecalculate(recalcEngine);
    return;
  }
  handleDhtmlxManualRecalculate();
}, [
  canMutateTasks,
  discarding,
  handleDhtmlxManualRecalculate,
  handleServerScheduleRecalculate,
  projectId,
  recalcEngine,
  saving,
  scheduleRecalcLoading,
]);


const leaveBlocker = useGanttLeaveGuard(
  useCallback(() => {
    const meta = store.getSessionMetadata();
    return meta.taskCount + meta.linkCount > 0;
  }, [store, sessionMetadata.taskCount, sessionMetadata.linkCount]),
);

const scheduleTasksForReadiness = useMemo(
  () => store.getTaskValues(),
  // readinessDataReady: re-read after hydrate so we don't flash needs_tasks on a stale []
  // eslint-disable-next-line react-hooks/exhaustive-deps -- store is stable; revisions via dhtmlxTasks
  [store, dhtmlxTasks, readinessDataReady],
);

const handleReadinessTip = useCallback(
  (state: 'needs_tasks' | 'needs_locations' | 'needs_assignment' | 'ready') => {
    if (state === 'ready') return;
    featureTips?.maybeShowTip('locations-and-tasks');
  },
  [featureTips?.maybeShowTip],
);

const readinessSuppressed =
  leaveBlocker.state === 'blocked' ||
  addTaskModalOpen ||
  isPanelOpen ||
  locationModalOpen ||
  deleteTaskTarget != null ||
  saveConfirmOpen ||
  discardConfirmOpen ||
  reassignConfirmOpen ||
  bulkUnknownTypeConfirmOpen ||
  baselineModalOpen ||
  liveScheduleOffModalOpen ||
  (ENABLE_TARGETS_QUANTITY_PANEL && targetsDiscardPending != null);

useEffect(() => {
  if (leaveBlocker.state !== 'blocked') return;
  if (addTaskModalOpen && !addTaskSubmitting) {
    closeAddTaskModal();
  }
  if (locationModalOpen) {
    setLocationModalOpen(false);
    setLocationModalTaskId(null);
  }
}, [
  leaveBlocker.state,
  addTaskModalOpen,
  addTaskSubmitting,
  locationModalOpen,
  closeAddTaskModal,
]);



/**
 * Format date for backend
 */
const formatDate = useCallback((date: Date | undefined): string | null => {
  if (!date) return null;
  return formatDateForBackend(date);
}, []);


const applyTaskTypeChange = useCallback(
  (taskId: string, value: string) => {
    perfMonitor.mark('task-type-update');
    taskMetaSyncInProgressRef.current = true;
    try {
      patchGanttTaskFields(taskId, { taskType: value });
      const metaItem = buildTaskMetaItem(taskId, value);
      if (metaItem.type === 'location' && metaItem.locationId) {
        const name = getLocationNameById(store.getLocationTree(), metaItem.locationId);
        patchGanttTaskFields(taskId, {
          locationId: metaItem.locationId,
          locationType: name ?? null,
        });
      }
      const updates: Partial<TaskSnapshot> = { taskType: value };
      if (metaItem.type === 'location' && metaItem.locationId) {
        updates.locationId = metaItem.locationId;
        updates.locationType =
          getLocationNameById(store.getLocationTree(), metaItem.locationId) ?? null;
      }
      store.updateTask(taskId, updates);
      // Type/location meta is saved immediately — do not mark as unsaved Save-to-Server delta.
      syncTaskMetaInBackground([metaItem], {
        onFailure: () => {
          store.addEditedTasks([taskId], columnKeysFromTaskPatch(updates));
        },
      });
      syncInheritedLocationDisplay();
    } finally {
      taskMetaSyncInProgressRef.current = false;
    }
    perfMonitor.measure('task-type-update');
  },
  [
    patchGanttTaskFields,
    buildTaskMetaItem,
    store,
    getLocationNameById,
    syncTaskMetaInBackground,
    syncInheritedLocationDisplay,
  ],
);

/**
 * Dump all accumulated changes to store (only touched tasks — never replace full dataset).
 */
const dumpAccumulatedChanges = useCallback(async () => {
  if (accumulatedChanges.current.size === 0) return;
  // Cascade leftovers must not run the small-edit dump path (overlay + per-row
  // addEditedTasks + mass refreshTask freezes ~16k for minutes).
  if (autoScheduleApplyingRef.current) {
    accumulatedChanges.current.clear();
    return;
  }

  const largeDump = accumulatedChanges.current.size > 80;
  if (!largeDump) {
    await beginLocalProcessing(
      'Updating schedule',
      'Applying your changes. Large projects can take a few seconds.',
    );
  }
  try {
  perfMonitor.mark('zustand-dump-accumulated');
  const updates = new Map(accumulatedChanges.current) as Map<string, Partial<TaskSnapshot>>;
  const changedTaskIds = Array.from(updates.keys());
  accumulatedChanges.current.clear();

  const taskIdsWithTypeChange = Array.from(updates.entries())
    .filter(([, ch]) => ch.taskType !== undefined)
    .map(([id]) => id);

  skipPendingHighlightRefreshRef.current = largeDump;
  store.batchUpdateTasks(updates);

  if (largeDump) {
    // One dirty-set write — never N× addEditedTasks (each triggers Zustand subscribers).
    store.addAffectedTasks(changedTaskIds);
  } else {
    // Immediate updateTaskMeta covers taskType/location — only mark other fields as unsaved.
    for (const [id, patch] of updates) {
      const pendingKeys = columnKeysFromTaskPatch(patch).filter(
        (key) => key !== 'taskType' && key !== 'locationType',
      );
      if (pendingKeys.length > 0) {
        store.addEditedTasks([id], pendingKeys);
      }
    }
  }

  // Grid Start/End/Duration templates read the store — refresh only small sets.
  const gantt = ganttRef.current?.instance as {
    isTaskExists?: (id: string) => boolean;
    refreshTask?: (id: string) => void;
  } | undefined;
  if (!largeDump && gantt?.refreshTask) {
    for (const id of changedTaskIds) {
      if (gantt.isTaskExists?.(id)) {
        gantt.refreshTask(id);
      }
    }
  }

  scheduleBackgroundPersist('accumulated-changes', changedTaskIds);

  if (taskIdsWithTypeChange.length > 0) {
    const locationTree = store.getLocationTree();
    const getTask = store.getTask.bind(store);

    const metaPayload = taskIdsWithTypeChange.map(taskId => {
      const task = store.getTask(taskId);
      const type = normalizeTaskType(task?.taskType) ?? 'unknown';
      const item: { taskId: string; type: string; locationId?: string } = { taskId, type };
      if (type === 'location') {
        const locationId = task?.locationId != null && String(task.locationId).trim() !== ''
          ? task.locationId
          : getEffectiveLocationIdForDisplay(getTask, taskId);
        item.locationId = locationId ?? '';
        if (locationId && (!task?.locationId || String(task.locationId).trim() === '')) {
          const name = getLocationNameById(locationTree, locationId);
          store.updateTask(taskId, { locationId: locationId ?? null, locationType: name ?? null });
        }
      }
      return item;
    });
    try {
      await updateTaskMeta(metaPayload);
    } catch {
      // Meta save failed — surface those fields as unsaved so Discard/Save stay accurate.
      for (const taskId of taskIdsWithTypeChange) {
        const patch = updates.get(taskId);
        if (!patch) continue;
        const metaKeys = columnKeysFromTaskPatch(patch).filter(
          (key) => key === 'taskType' || key === 'locationType',
        );
        if (metaKeys.length > 0) {
          store.addEditedTasks([taskId], metaKeys);
        }
      }
    }
  }

  perfMonitor.measure('zustand-dump-accumulated');
  } finally {
    endLocalProcessing();
  }
}, [store, projectId, getLocationNameById, scheduleBackgroundPersist, beginLocalProcessing, endLocalProcessing]);

/**
 * Handle drag start
 */
const handleBeforeTaskDrag = useCallback((id: string | number, mode: string) => {
  const taskId = String(id);
  const gantt = ganttRef.current?.instance as {
    getTask?: (id: string | number) => { type?: string } | null;
    hasChild?: (id: string | number) => boolean;
    getChildren?: (id: string | number) => unknown[];
  } | undefined;
  const task = gantt?.getTask?.(id);
  const snap = store.getTask(taskId);
  const childIds = typeof gantt?.getChildren === 'function' ? gantt.getChildren(id) : [];
  const hasChildren =
    parentIdsWithChildren.has(taskId) ||
    (Array.isArray(childIds) && childIds.length > 0) ||
    !!gantt?.hasChild?.(id);
  const isParent =
    hasChildren || task?.type === 'project' || !!snap?.summary;

  // Match grid / task details: progress is editable on leaf tasks only.
  if (mode === 'progress' && isParent) {
    return false;
  }

  // Cancel any pending dump
  if (dumpTimer.current) {
    clearTimeout(dumpTimer.current);
    dumpTimer.current = null;
  }

  isDragging.current = true;
  accumulatedChanges.current.clear();
  store.startBatchRecording();
  perfMonitor.mark('zustand-task-drag');
  return true;
}, [store, parentIdsWithChildren]);

/**
 * Handle drag end - dump all changes at once
 */
const handleAfterTaskDrag = useCallback(async (id: string | number, mode: string) => {
  if (!isDragging.current) return;

  isDragging.current = false;
  store.stopBatchRecording();
  perfMonitor.measure('zustand-task-drag');

  if (autoScheduleEnabled) {
    // dhtmlx auto-schedule will fire onAfterAutoSchedule; avoid full-dataset dump race.
    if (accumulatedChanges.current.size > 0) {
      if (dumpTimer.current) {
        clearTimeout(dumpTimer.current);
      }
      dumpTimer.current = setTimeout(() => {
        void dumpAccumulatedChanges();
        dumpTimer.current = null;
      }, 600);
    }
  } else {
    await dumpAccumulatedChanges();
  }

  logPerfSummary();
}, [autoScheduleEnabled, dumpAccumulatedChanges]);

/**
 * Handle task update (during drag or edit).
 * Only accumulate fields that actually changed — DHTMLX fires onAfterTaskUpdate on
 * inline-editor blur even when the value is unchanged.
 *
 * End dates: grid End editor calendar day is inclusive MSP Finish; otherwise treat
 * gantt end_date as exclusive and convert before writing the store.
 */
const handleTaskUpdate = useCallback((id: string | number, task: any) => {
  // Cascade schedule fields are applied once in handleAutoSchedule — skip per-task storms.
  if (autoScheduleApplyingRef.current) return;
  // Intentional location/taskType meta sync saves via updateTaskMeta — skip pending leak.
  if (taskMetaSyncInProgressRef.current) return;

  const taskId = String(id);
  const existing = store.getTask(taskId);
  const gantt = ganttRef.current?.instance as any;

  if (!existing) return;

  const changes: Partial<TaskSnapshot> = {};
  const fromEndEditor = endDateEditorCommitRef.current;
  if (fromEndEditor) {
    endDateEditorCommitRef.current = false;
  }

  if (task.text !== undefined && task.text !== resolveTaskName(existing)) {
    const trimmed = String(task.text ?? '').trim();
    if (!trimmed) {
      // Keep prior name — empty rename is not allowed.
      task.text = resolveTaskName(existing);
      if (gantt?.refreshTask) {
        gantt.refreshTask(taskId);
      }
    } else {
      changes.taskName = trimmed;
      if (trimmed !== String(task.text)) {
        task.text = trimmed;
      }
    }
  }

  // Date editors are day-granular; keep existing time-of-day so midnight rewrites
  // (HTML5 / typed date) do not inflate the Save counter when the calendar day is unchanged.
  const newStart = task.start_date ? formatDate(task.start_date) : null;
  if (newStart && !sameCalendarDay(newStart, existing.start)) {
    changes.start = applyCalendarDayKeepingTime(task.start_date, existing.start);
  }

  const inclusiveFinish = task.end_date
    ? exclusiveEndToInclusiveFinishWallClock(task.end_date, existing.finish, {
        fromEndEditor,
      })
    : null;
  if (inclusiveFinish && !sameCalendarDay(inclusiveFinish, existing.finish)) {
    changes.finish = inclusiveFinish;
  }

  if (task.duration !== undefined) {
    const d = Number(task.duration);
    if (Number.isFinite(d) && d !== existing.duration) {
      changes.duration = d;
    }
  }

  // When start/finish moved (drag or date edit), re-derive inclusive working-day duration.
  if (changes.start !== undefined || changes.finish !== undefined) {
    const startForDuration = changes.start ?? existing.start ?? task.start_date;
    const finishForDuration = changes.finish ?? existing.finish ?? inclusiveFinish;
    if (startForDuration && finishForDuration) {
      const derivedDuration = computeDurationFromStartAndFinish(
        startForDuration,
        finishForDuration,
      );
      if (Number.isFinite(derivedDuration) && derivedDuration !== existing.duration) {
        changes.duration = derivedDuration;
      }
    }
  }

  // Duration-only edits update end_date in Gantt — keep store finish in sync (exclusive→inclusive).
  if (changes.duration !== undefined && changes.finish === undefined && task.end_date) {
    const finishFromExclusive = exclusiveEndToInclusiveFinishWallClock(
      task.end_date,
      existing.finish,
    );
    if (finishFromExclusive && !sameCalendarDay(finishFromExclusive, existing.finish)) {
      changes.finish = finishFromExclusive;
    }
  }

  // Keep live gantt start/end/duration agreeing (MSP inclusive → exclusive end) so
  // Live Schedule does not resurrect the previous duration after an End edit.
  if (
    changes.start !== undefined ||
    changes.finish !== undefined ||
    changes.duration !== undefined
  ) {
    const schedule = buildDhtmlxScheduleFields({
      start: changes.start ?? existing.start,
      finish: changes.finish ?? existing.finish,
      duration: changes.duration ?? existing.duration,
      milestone: existing.milestone,
    });
    if (schedule.start_date) task.start_date = schedule.start_date;
    if (schedule.end_date) task.end_date = schedule.end_date;
    if (schedule.duration != null) task.duration = schedule.duration;
  }

  if (task.progress !== undefined) {
    const percentComplete = Math.round(task.progress * 100);
    if (percentComplete !== existing.percentComplete) {
      changes.percentComplete = percentComplete;
    }
  }

  if (task.taskType !== undefined && task.taskType !== existing.taskType) {
    changes.taskType = task.taskType;
  }

  if (Object.keys(changes).length > 0) {
    // Accumulate all changes (drag or non-drag)
    accumulatedChanges.current.set(taskId, {
      ...accumulatedChanges.current.get(taskId),
      ...changes,
    });

    if (!isDragging.current) {
      // Apply to store immediately so grid templates (store-backed Start/End/Duration)
      // match Task Details without waiting for the debounced dump.
      store.updateTask(taskId, changes);
      // taskType/locationType are saved via updateTaskMeta — never mark them pending here.
      const pendingKeys = columnKeysFromTaskPatch(changes).filter(
        (key) => key !== 'taskType' && key !== 'locationType',
      );
      if (pendingKeys.length > 0) {
        store.addEditedTasks([taskId], pendingKeys);
      }
      if (gantt?.refreshTask) {
        gantt.refreshTask(taskId);
      }

      // Debounced dump still batches IDB persist / meta side-effects.
      if (dumpTimer.current) {
        clearTimeout(dumpTimer.current);
      }
      dumpTimer.current = setTimeout(() => {
        dumpAccumulatedChanges();
        dumpTimer.current = null;
      }, 300); // 300ms debounce
    }
    // During drag, changes accumulate and dump happens at drag end
  }
}, [store, formatDate, dumpAccumulatedChanges]);

/**
 * Track branch expand/collapse so newly added children stay visible under their parent.
 */
useEffect(() => {
  let openHandlerId: number | string | null = null;
  let closeHandlerId: number | string | null = null;
  const timer = window.setTimeout(() => {
    const gantt = ganttRef.current?.instance as {
      attachEvent?: (name: string, handler: (id: string | number) => boolean) => number | string;
    } | undefined;
    if (!gantt?.attachEvent) return;

    openHandlerId = gantt.attachEvent('onTaskOpened', (id: string | number) => {
      const taskId = String(id);
      setCollapsedBranchIds((prev: Set<string>) => {
        if (!prev.has(taskId)) return prev;
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      return true;
    });

    closeHandlerId = gantt.attachEvent('onTaskClosed', (id: string | number) => {
      const taskId = String(id);
      setCollapsedBranchIds((prev: Set<string>) => {
        if (prev.has(taskId)) return prev;
        const next = new Set(prev);
        next.add(taskId);
        return next;
      });
      return true;
    });
  }, 400);

  return () => {
    window.clearTimeout(timer);
    const gantt = ganttRef.current?.instance as {
      detachEvent?: (id: number | string) => void;
    } | undefined;
    if (gantt?.detachEvent) {
      if (openHandlerId != null) gantt.detachEvent(openHandlerId);
      if (closeHandlerId != null) gantt.detachEvent(closeHandlerId);
    }
  };
}, []);

/**
 * Block parent/ancestor (incl. milestone parent) and descendant predecessors before
 * DHTMLX accepts the link — surfaces a Mantine toast instead of a silent engine loop.
 */
useEffect(() => {
  let beforeLinkAddId: number | string | null = null;
  let circularId: number | string | null = null;
  const timer = window.setTimeout(() => {
    const gantt = ganttRef.current?.instance as {
      attachEvent?: (
        name: string,
        handler: (...args: unknown[]) => boolean | void,
      ) => number | string;
    } | undefined;
    if (!gantt?.attachEvent) return;

    beforeLinkAddId = gantt.attachEvent('onBeforeLinkAdd', (_id: unknown, link: unknown) => {
      const row = link as { source?: string | number; target?: string | number } | null;
      if (!row?.source || !row?.target) return true;
      const check = validatePredecessorLink(
        String(row.source),
        String(row.target),
        store.getTaskValues(),
        store.getLinks(),
      );
      if (!check.ok) {
        notifications.show({
          title: 'Invalid predecessor',
          message: check.reason,
          color: 'red',
        });
        return false;
      }
      return true;
    });

    circularId = gantt.attachEvent('onCircularLinkError', () => {
      notifications.show({
        title: 'Invalid predecessor',
        message:
          'That dependency would create a circular schedule loop. Choose a different predecessor.',
        color: 'red',
      });
    });
  }, 400);

  return () => {
    window.clearTimeout(timer);
    const gantt = ganttRef.current?.instance as {
      detachEvent?: (id: number | string) => void;
    } | undefined;
    if (gantt?.detachEvent) {
      if (beforeLinkAddId != null) gantt.detachEvent(beforeLinkAddId);
      if (circularId != null) gantt.detachEvent(circularId);
    }
  };
}, [store, calendarReady]);

/**
 * Cleanup timer on unmount
 */
useEffect(() => {
  return () => {
    if (dumpTimer.current) {
      clearTimeout(dumpTimer.current);
    }
  };
}, []);

/**
 * After link graph changes, React Gantt incremental updates can wipe $source/$target.
 * Cascade schedule refreshes run synchronously in handleAutoSchedule (tasks array may
 * keep identity, so this effect must not be the only refresh path).
 */
useEffect(() => {
  const gantt = ganttRef.current?.instance as {
    getLinks?: () => unknown[];
    eachTask?: (cb: (task: { $source?: unknown[]; $target?: unknown[] }) => void) => void;
    getTask?: (id: string | number) => { $source?: unknown[]; $target?: unknown[] };
    isTaskExists?: (id: string | number) => boolean;
    batchUpdate?: (cb: () => void) => void;
  } | undefined;
  if (
    !gantt ||
    typeof gantt.getLinks !== 'function' ||
    typeof gantt.eachTask !== 'function' ||
    typeof gantt.getTask !== 'function' ||
    typeof gantt.isTaskExists !== 'function'
  ) {
    return;
  }

  const revision = store.getLinksRevision();
  if (revision === lastLinkPointersRevisionRef.current) {
    return;
  }
  lastLinkPointersRevisionRef.current = revision;

  const run = () => repopulateGanttLinkPointers(gantt as any);
  if (typeof gantt.batchUpdate === 'function') {
    gantt.batchUpdate(run);
  } else {
    run();
  }
}, [linksRevision, store]);

/**
 * Attach inline editor handlers: predecessor $target sync + inline editor (Sr No codes),
 * Location cell → open panel, Task name edit only via double-click.
 * Predecessor picker opens via the cell/editor pick button.
 */
useEffect(() => {
  let beforeEditHandlerId: number | string | null = null;
  let editStartHandlerId: number | string | null = null;
  let columnResizeStartHandlerId: number | string | null = null;
  let columnResizeEndHandlerId: number | string | null = null;
  let columnResizeCompleteHandlerId: number | string | null = null;
  let gridResizeEndHandlerId: number | string | null = null;
  let originalGetEditorConfig: ((columnName: string) => unknown) | null = null;
  const timer = setTimeout(() => {
    const gantt = ganttRef.current?.instance as any;
    if (!gantt) return;

    // Persist grid↔timeline divider share for this tab session.
    if (typeof gantt.attachEvent === 'function') {
      gridResizeEndHandlerId = gantt.attachEvent(
        'onGridResizeEnd',
        (_oldWidth: number, newWidth: number) => {
          try {
            if (typeof newWidth === 'number' && newWidth > 0) {
              rememberScheduleGridWidth(newWidth);
              const rootWidth = Number(gantt.$root?.clientWidth ?? 0);
              if (projectId && rootWidth > 0) {
                saveGanttGridSplit(
                  projectId,
                  gridSharePercentFromWidths(rootWidth, newWidth),
                );
              }
            }
          } catch {
            // ignore
          }
          return true;
        },
      );
    }

    if (!gantt.ext?.inlineEditors) return;

    // Replace native <input type="date"> with typeable DD/MM/YYYY + calendar button.
    installGanttTypedDateEditor(gantt as { config?: { editor_types?: Record<string, unknown> } });

    const inlineEditors = gantt.ext.inlineEditors as any;

    const endInlineEditingForLayoutChange = () => {
      try {
        if (!inlineEditors.isVisible?.()) return;
        // Commit real edits; otherwise just dismiss so the placeholder doesn't paint at the old width.
        if (inlineEditors.isChanged?.()) {
          inlineEditors.save?.();
        }
        inlineEditors.hide?.();
      } catch {
        // ignore
      }
    };

    // Hide Task name editor from single-click path; still available for startEdit after dblclick.
    originalGetEditorConfig = inlineEditors.getEditorConfig?.bind(inlineEditors) ?? null;
    if (originalGetEditorConfig) {
      inlineEditors.getEditorConfig = (columnName: string) => {
        const cfg = originalGetEditorConfig!(columnName);
        if (columnName !== 'text') return cfg;
        const state = inlineEditors.getState?.();
        if (inlineEditors.isVisible?.() && state?.columnName === 'text') return cfg;
        if (taskNameInlineEditAllowedRef.current) return cfg;
        return null;
      };
    }

    beforeEditHandlerId = inlineEditors.attachEvent(
      'onBeforeEditStart',
      (cell: { id: string | number; columnName: string }) => {
        if (cell?.columnName === 'taskType') {
          return false; /* custom dropdown in template; no inline editor */
        }
        if (cell?.columnName === 'locationType') {
          openLocationModalRef.current?.(String(cell.id));
          setTimeout(closeLocationInlineEditor, 0);
          return false; /* never show inline editor for Location name column */
        }
        if (cell?.columnName === 'predecessors') {
          const taskId = String(cell.id);
          const snap = store.getTask(taskId);
          const ganttTask = gantt.getTask?.(cell.id) as { type?: string } | undefined;
          const childIds =
            typeof gantt.getChildren === 'function' ? gantt.getChildren(cell.id) : [];
          const hasChildren =
            (Array.isArray(childIds) && childIds.length > 0) ||
            !!gantt.hasChild?.(cell.id) ||
            parentIdsWithChildren.has(taskId);
          const isParent =
            hasChildren || ganttTask?.type === 'project' || !!snap?.summary;
          // Summary/parent rows: predecessors are display-only (same as task details).
          if (isParent) return false;
          // Leaf: sync $target then allow DHTMLX predecessor inline editor.
          const task = gantt.getTask?.(cell.id);
          const allLinks = typeof gantt.getLinks === 'function' ? gantt.getLinks() : [];
          const predLinks = allLinks.filter((l: { target?: string | number }) => l.target == cell.id);
          if (task) {
            (task as { $target?: unknown[] }).$target = predLinks.map(
              (l: { id?: string | number }) => l.id,
            );
          }
          // Prefer Sr No while the predecessor editor formats/parses link tokens.
          if (!predecessorSrNoCodeOverrideRef.current.active && typeof gantt.getWBSCode === 'function') {
            const original = gantt.getWBSCode.bind(gantt) as (task: unknown) => string;
            predecessorSrNoCodeOverrideRef.current = { active: true, original };
            gantt.getWBSCode = (row: { id?: string | number }) => {
              const id = row?.id != null ? String(row.id) : '';
              const taskSnap = id ? store.getTask(id) : undefined;
              const srNo = taskSnap?.srNo != null ? String(taskSnap.srNo).trim() : '';
              if (srNo) return srNo;
              return original(row);
            };
          }
          return true;
        }
        if (cell?.columnName === 'text') {
          // Flag is set by onTaskDblClick; cleared after startEdit returns.
          return taskNameInlineEditAllowedRef.current;
        }
        if (
          cell?.columnName === 'start_date' ||
          cell?.columnName === 'end_date' ||
          cell?.columnName === 'duration'
        ) {
          const taskId = String(cell.id);
          const task = gantt.getTask?.(cell.id);
          const snap = store.getTask(taskId);
          const childIds =
            typeof gantt.getChildren === 'function' ? gantt.getChildren(cell.id) : [];
          const hasChildren =
            (Array.isArray(childIds) && childIds.length > 0) ||
            !!gantt.hasChild?.(cell.id) ||
            parentIdsWithChildren.has(taskId);
          const isParent =
            hasChildren || task?.type === 'project' || !!snap?.summary;
          if (isParent) return false; /* Start/End/Duration editable for leaf tasks only */

          // Align gantt start/end/duration with store inclusive values so the
          // editor seeds the same numbers the idle cell templates show
          // (DHTMLX work_time + exclusive end often yields duration − 1).
          if (
            (cell.columnName === 'start_date' ||
              cell.columnName === 'end_date' ||
              cell.columnName === 'duration') &&
            snap &&
            task
          ) {
            const schedule = buildDhtmlxScheduleFields({
              start: snap.start,
              finish: snap.finish,
              duration: snap.duration,
              milestone: snap.milestone,
            });
            if (schedule.start_date) task.start_date = schedule.start_date;
            if (schedule.end_date) task.end_date = schedule.end_date;
            if (schedule.duration != null) task.duration = schedule.duration;
          }
        }
        return true;
      },
    );
    editStartHandlerId = inlineEditors.attachEvent(
      'onEditStart',
      (cell: { id: string | number; columnName: string }) => {
        if (cell?.columnName === 'locationType') {
          setTimeout(closeLocationInlineEditor, 0);
          return false;
        }
        if (cell?.columnName === 'predecessors') {
          window.requestAnimationFrame(() => {
            const placeholder = document.querySelector(
              '.gantt_grid_editor_placeholder[data-column-name="predecessors"]',
            ) as HTMLElement | null;
            if (!placeholder || placeholder.querySelector('.gantt-predecessor-pick-btn')) return;
            placeholder.classList.add('gantt-predecessor-editor-host');
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'gantt-predecessor-pick-btn gantt-predecessor-pick-btn--editor';
            btn.title = 'Browse tasks';
            btn.setAttribute('aria-label', 'Browse predecessor tasks');
            btn.setAttribute('data-task-id', String(cell.id));
            btn.innerHTML =
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
              '<path d="M10 3H3v7h7V3zM21 3h-7v7h7V3zM21 14h-7v7h7v-7zM10 14H3v7h7v-7z"/>' +
              '</svg>';
            btn.addEventListener('mousedown', (e) => {
              e.preventDefault();
              e.stopPropagation();
            });
            placeholder.appendChild(btn);
          });
          return true;
        }
        // Typed date editor: seed from store inclusive day; picker only via button/dblclick.
        if (cell?.columnName === 'start_date' || cell?.columnName === 'end_date') {
          if (cell.columnName === 'end_date') {
            endDateEditorCommitRef.current = true;
          }
          window.requestAnimationFrame(() => {
            const root = document.querySelector(
              `.gantt_grid_editor_placeholder[data-column-name="${cell.columnName}"] .gantt-date-editor`,
            ) as (HTMLElement & {
              __ganttDateEditor?: {
                text: HTMLInputElement;
                native: HTMLInputElement;
                originalDay: string | null;
                keydownHandler?: (e: KeyboardEvent) => void;
              };
            }) | null;
            const eds = root?.__ganttDateEditor;
            if (!eds) return;

            const snap = store.getTask(String(cell.id));
            const ganttTask = gantt.getTask?.(cell.id) as
              | { start_date?: Date; end_date?: Date }
              | undefined;
            const seedDay = resolveDateEditorSeedDay({
              columnName: cell.columnName,
              storeStart: snap?.start,
              storeFinish: snap?.finish,
              ganttStart: ganttTask?.start_date,
              ganttEnd: ganttTask?.end_date,
            });
            if (seedDay) {
              applyInclusiveDayToDateEditor(eds, seedDay);
            }

            const commitFromPicker = () => {
              try {
                if (cell.columnName === 'end_date') {
                  endDateEditorCommitRef.current = true;
                }
                if (inlineEditors.isChanged?.()) {
                  inlineEditors.save?.();
                }
                inlineEditors.hide?.();
              } catch {
                // ignore
              }
            };
            eds.native.addEventListener('change', commitFromPicker, { once: true });

            // Escape cancels without saving.
            if (eds.keydownHandler) {
              eds.text.removeEventListener('keydown', eds.keydownHandler);
            }
            const onKeyDown = (e: KeyboardEvent) => {
              if (e.key !== 'Escape') return;
              e.preventDefault();
              e.stopPropagation();
              try {
                if (eds.originalDay) {
                  applyInclusiveDayToDateEditor(eds, eds.originalDay);
                }
                inlineEditors.hide?.();
              } catch {
                // ignore
              }
            };
            eds.keydownHandler = onKeyDown;
            eds.text.addEventListener('keydown', onKeyDown);
            eds.text.focus();
            eds.text.select();
          });
        }
        // Duration number: seed store duration into the input (belt-and-suspenders
        // if DHTMLX already read a stale work_time duration), then commit on change.
        if (cell?.columnName === 'duration') {
          window.requestAnimationFrame(() => {
            const input = document.querySelector(
              `.gantt_grid_editor_placeholder[data-column-name="duration"] input`,
            ) as HTMLInputElement | null;
            if (!input) return;
            const snap = store.getTask(String(cell.id));
            const storeDuration =
              snap?.duration != null && Number.isFinite(Number(snap.duration))
                ? Math.round(Number(snap.duration))
                : null;
            if (storeDuration != null && input.value !== String(storeDuration)) {
              input.value = String(storeDuration);
            }
            const commit = () => {
              try {
                if (inlineEditors.isChanged?.()) {
                  inlineEditors.save?.();
                }
                inlineEditors.hide?.();
              } catch {
                // ignore
              }
            };
            input.addEventListener('change', commit, { once: true });
          });
        }
        return true;
      },
    );

    inlineEditors.attachEvent?.(
      'onEditEnd',
      (cell: { columnName?: string }) => {
        if (cell?.columnName === 'predecessors') {
          const override = predecessorSrNoCodeOverrideRef.current;
          if (override.active && override.original && gantt) {
            gantt.getWBSCode = override.original;
            predecessorSrNoCodeOverrideRef.current = { active: false, original: null };
          }
        }
        if (cell?.columnName === 'end_date') {
          // Clear leftover flag if the user cancels; after a save, handleTaskUpdate
          // already consumed the flag synchronously before this timeout.
          window.setTimeout(() => {
            endDateEditorCommitRef.current = false;
          }, 0);
        }
      },
    );

    // Column resize leaves the inline-editor placeholder at the old width — dismiss on start.
    // dhtmlx calls onColumnResizeEnd(index, column, newWidth) *before* assigning column.width,
    // so we must persist `newWidth` (not col.width). onColumnResizeComplete runs after apply.
    if (typeof gantt.attachEvent === 'function') {
      const persistResizedColumnWidths = (
        columns: Array<{ name?: string; width?: number }> | null | undefined,
        override?: { name: string; width: number },
      ) => {
        const alreadyPinned = hasPinnedGanttColumnWidths(columnWidthsRef.current);
        const next: Partial<Record<string, number>> = { ...columnWidthsRef.current };
        if (
          override &&
          isPersistableGanttColumnName(override.name) &&
          override.width > 0
        ) {
          next[override.name] = Math.round(override.width);
        }
        for (const col of columns ?? []) {
          const name = col?.name;
          if (
            name &&
            isPersistableGanttColumnName(name) &&
            typeof col.width === 'number' &&
            col.width > 0
          ) {
            // Prefer explicit override for the resized column when width is still stale.
            if (override && name === override.name) continue;
            next[name] = Math.round(col.width);
          }
        }
        columnWidthsRef.current = next;
        if (projectId) {
          saveGanttColumnWidths(projectId, next);
        }
        // Re-assert fill vs scroll without rebuilding React columns mid-session
        // (except the first time we pin anything, so initial config picks up widths).
        const gInstance = ganttRef.current?.instance as Parameters<
          typeof applyScheduleGridColumnLayout
        >[0];
        if (gInstance && !columnResizeInProgressRef.current) {
          const rootW =
            (gInstance as { $root?: { clientWidth?: number } }).$root?.clientWidth ?? 0;
          applyScheduleGridColumnLayout(gInstance, {
            liveWidths: next,
            preferredGridWidth: resolveScheduleGridWidth(rootW, projectId),
            skipRender: true,
          });
        }
        // Only rebuild React config the first time we pin — bumping on every resize
        // re-runs layout effects and fights the resize interaction.
        if (!alreadyPinned && hasPinnedGanttColumnWidths(next)) {
          setColumnWidthsVersion((v: number) => v + 1);
        }
      };

      columnResizeStartHandlerId = gantt.attachEvent('onColumnResizeStart', () => {
        columnResizeInProgressRef.current = true;
        endInlineEditingForLayoutChange();
        return true;
      });
      columnResizeEndHandlerId = gantt.attachEvent(
        'onColumnResizeEnd',
        (_index: number, column: { name?: string; width?: number } | null, newWidth: number) => {
          try {
            const name = column?.name != null ? String(column.name) : '';
            if (
              isPersistableGanttColumnName(name) &&
              typeof newWidth === 'number' &&
              Number.isFinite(newWidth) &&
              newWidth > 0
            ) {
              persistResizedColumnWidths(null, { name, width: newWidth });
            }
          } catch {
            // ignore
          }
          // Complete usually clears this; clear here too if complete is skipped.
          window.setTimeout(() => {
            columnResizeInProgressRef.current = false;
            const gInstance = ganttRef.current?.instance as Parameters<
              typeof applyScheduleGridColumnLayout
            >[0];
            if (gInstance) {
              const rootW =
                (gInstance as { $root?: { clientWidth?: number } }).$root?.clientWidth ?? 0;
              applyScheduleGridColumnLayout(gInstance, {
                liveWidths: columnWidthsRef.current,
                preferredGridWidth: resolveScheduleGridWidth(rootW, projectId),
                skipRender: true,
              });
              gInstance.setSizes?.();
            }
          }, 0);
          return true;
        },
      );
      columnResizeCompleteHandlerId = gantt.attachEvent(
        'onColumnResizeComplete',
        (columns: Array<{ name?: string; width?: number }>) => {
          try {
            // Apply after drag ends so fill/scroll can update without fighting the gesture.
            columnResizeInProgressRef.current = false;
            persistResizedColumnWidths(columns);
          } catch {
            // ignore
          } finally {
            columnResizeInProgressRef.current = false;
          }
          return true;
        },
      );
    }
  }, 400);

  return () => {
    clearTimeout(timer);
    const g = ganttRef.current?.instance as any;
    const override = predecessorSrNoCodeOverrideRef.current;
    if (override.active && override.original && g) {
      g.getWBSCode = override.original;
      predecessorSrNoCodeOverrideRef.current = { active: false, original: null };
    }
    if (g?.ext?.inlineEditors) {
      if (originalGetEditorConfig) {
        g.ext.inlineEditors.getEditorConfig = originalGetEditorConfig;
      }
      if (beforeEditHandlerId != null) g.ext.inlineEditors.detachEvent(beforeEditHandlerId);
      if (editStartHandlerId != null) g.ext.inlineEditors.detachEvent(editStartHandlerId);
    }
    if (g && typeof g.detachEvent === 'function') {
      if (columnResizeStartHandlerId != null) g.detachEvent(columnResizeStartHandlerId);
      if (columnResizeEndHandlerId != null) g.detachEvent(columnResizeEndHandlerId);
      if (columnResizeCompleteHandlerId != null) g.detachEvent(columnResizeCompleteHandlerId);
      if (gridResizeEndHandlerId != null) g.detachEvent(gridResizeEndHandlerId);
    }
  };
}, [closeLocationInlineEditor, parentIdsWithChildren, store, projectId]);

/**
 * Custom Task type dropdown: update store and schedule meta sync on change.
 * (Task type column uses template-rendered <select>, not the grid's default editor.)
 */
useEffect(() => {
  const changeHandler = (e: Event) => {
    const el = e.target as HTMLElement;
    if (!el?.classList?.contains('gantt-task-type-select')) return;
    const taskId = el.getAttribute('data-task-id');
    const value = (el as HTMLSelectElement).value;
    if (!taskId || value == null) return;
    applyTaskTypeChange(taskId, value);
  };
  document.addEventListener('change', changeHandler, true);
  return () => document.removeEventListener('change', changeHandler, true);
}, [applyTaskTypeChange]);

/**
 * Open Location Hierarchy when the user clicks the Location name cell.
 * - Document-level delegation for .gantt-open-location (template span).
 * - Inline editor onBeforeEditStart above opens panel and returns false for locationType (no input).
 * Known intermittent issue: after idle time or other actions, Location cell click may only select the row
 * instead of opening the panel. Suspect: gantt/inlineEditors re-init or DOM refresh detaching our handlers.
 * If it recurs, consider re-attaching onBeforeEditStart/onEditStart when gantt instance or data updates.
 */
useEffect(() => {
  const handler = (e: MouseEvent) => {
    const el = (e.target as HTMLElement)?.closest?.('.gantt-open-location');
    if (!el) return;
    const taskId = el.getAttribute('data-task-id');
    if (!taskId) return;
    e.preventDefault();
    e.stopPropagation();
    openLocationModalRef.current?.(taskId);
  };
  document.addEventListener('click', handler, true);
  return () => document.removeEventListener('click', handler, true);
}, []);

/**
 * Predecessor pick button (idle cell or inline editor) → browse tree picker.
 * Value area remains single-click inline edit.
 */
useEffect(() => {
  const stopEditStart = (e: MouseEvent) => {
    const el = (e.target as HTMLElement)?.closest?.('.gantt-predecessor-pick-btn');
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
  };
  const openPicker = (e: MouseEvent) => {
    const el = (e.target as HTMLElement)?.closest?.('.gantt-predecessor-pick-btn');
    if (!el) return;
    const taskId = el.getAttribute('data-task-id');
    if (!taskId) return;
    e.preventDefault();
    e.stopPropagation();
    const snap = store.getTask(taskId);
    if (snap?.summary || parentIdsWithChildren.has(taskId)) return;
    dismissGanttInlineEditing();
    openPredecessorPickerRef.current?.(taskId, 'grid');
  };
  // mousedown: block DHTMLX from starting inline edit when using the icon.
  document.addEventListener('mousedown', stopEditStart, true);
  document.addEventListener('click', openPicker, true);
  return () => {
    document.removeEventListener('mousedown', stopEditStart, true);
    document.removeEventListener('click', openPicker, true);
  };
}, [store, parentIdsWithChildren, dismissGanttInlineEditing]);


/**
 * Open task details panel/drawer for a task and highlight it in the grid.
 */
const openTaskDetailsPanel = useCallback((
  id: string | number,
  options?: { scrollToLinkedIssues?: boolean },
) => {
  const taskId = String(id);
  // Avoid two right-side drawers: close grid predecessor picker when details open.
  closePredecessorPicker();
  store.setSelectedTaskId(taskId);
  if (options?.scrollToLinkedIssues) {
    setScrollToLinkedIssuesRequest((n: number) => n + 1);
  }
  const gantt = ganttRef.current?.instance as {
    selectTask?: (taskId: string | number) => void;
    isTaskExists?: (taskId: string | number) => boolean;
  } | undefined;
  if (gantt?.selectTask && gantt.isTaskExists?.(taskId)) {
    gantt.selectTask(taskId);
  }
}, [store, closePredecessorPicker]);
openTaskDetailsPanelRef.current = openTaskDetailsPanel;

/**
 * When details are already open, clicking another grid row switches the panel to that task.
 */
const handleTaskSelected = useCallback(
  (id: string | number) => {
    const current = store.getSelectedTaskId();
    if (!current) return;
    const next = String(id);
    if (next !== current) {
      store.setSelectedTaskId(next);
    }
  },
  [store],
);

/** Keep gantt row selection aligned with the open task details selection. */
useEffect(() => {
  const gantt = ganttRef.current?.instance as {
    selectTask?: (taskId: string | number) => void;
    unselectTask?: (taskId: string | number) => void;
    getSelectedId?: () => string | number | null;
    isTaskExists?: (taskId: string | number) => boolean;
  } | undefined;
  if (!gantt) return;

  const currentId = gantt.getSelectedId?.();
  if (selectedTaskId && gantt.isTaskExists?.(selectedTaskId)) {
    if (String(currentId ?? '') !== selectedTaskId) {
      gantt.selectTask?.(selectedTaskId);
    }
    return;
  }

  if (currentId != null && currentId !== '') {
    gantt.unselectTask?.(currentId);
  }
}, [selectedTaskId, tasks]);

/**
 * Add / delete task actions in the Task name grid column (template buttons).
 */
useEffect(() => {
  const handler = (e: MouseEvent) => {
    const selectAll = (e.target as HTMLElement)?.closest?.('.gantt-assign-select-all');
    if (selectAll instanceof HTMLInputElement) {
      e.stopPropagation();
      if (selectAll.checked) selectVisibleLeavesForAssign();
      else deselectVisibleLeavesForAssign();
      return;
    }
    const assignCb = (e.target as HTMLElement)?.closest?.('.gantt-assign-select-checkbox');
    if (assignCb instanceof HTMLInputElement) {
      e.stopPropagation();
      const taskId = assignCb.getAttribute('data-task-id');
      if (taskId) toggleAssignTaskSelected(taskId, assignCb.checked);
      return;
    }
    const assigneesCell = (e.target as HTMLElement)?.closest?.('.gantt-assignees-cell');
    if (assigneesCell) {
      e.preventDefault();
      e.stopPropagation();
      const taskId = assigneesCell.getAttribute('data-task-id');
      if (!taskId) return;
      store.setSelectedTaskId(taskId);
      const { chips } = buildAssignTargetChipsFromTaskIds({
        taskIds: [taskId],
        getTask: (id) => store.getTask(id) ?? null,
        effectiveLocationIdByTaskId,
        locationOptions: assignLocationOptions,
        taskCatalog: assignTaskCatalog,
        normalizeTaskType,
        resolveTaskName,
      });
      openAssignUsersDrawer({
        initialTargets: chips,
        presentation: 'modal',
        targetsLocked: true,
        mode: assignMockEnabled ? 'mock' : 'live',
      });
      return;
    }
    const editBtn = (e.target as HTMLElement)?.closest?.('.gantt-task-edit-btn');
    if (editBtn) {
      e.preventDefault();
      e.stopPropagation();
      const taskId = editBtn.getAttribute('data-task-id');
      if (taskId) {
        openTaskDetailsPanel(taskId);
      }
      return;
    }
    const issueCountBtn = (e.target as HTMLElement)?.closest?.('.gantt-task-issue-count');
    if (issueCountBtn) {
      e.preventDefault();
      e.stopPropagation();
      const taskId = issueCountBtn.getAttribute('data-task-id');
      if (taskId) {
        openTaskDetailsPanel(taskId, { scrollToLinkedIssues: true });
      }
      return;
    }
    const addBtn = (e.target as HTMLElement)?.closest?.('.gantt-task-add-btn');
    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();
      if (addBtn.classList.contains('is-disabled') || !canTaskCreate) return;
      const taskId = addBtn.getAttribute('data-task-id');
      openAddTaskModal(taskId || null);
      return;
    }
    const delBtn = (e.target as HTMLElement)?.closest?.('.gantt-task-delete-btn');
    if (!delBtn || delBtn.classList.contains('is-disabled')) return;
    if (!canTaskDelete) return;
    e.preventDefault();
    e.stopPropagation();
    const taskId = delBtn.getAttribute('data-task-id');
    if (!taskId) return;
    const task = store.getTask(taskId);
    if (!task) return;
    if (taskHasChildren(taskId, store.getTasks())) {
      notifications.show({
        title: 'Cannot delete',
        message: 'This task has child tasks. Remove or move children before deleting.',
        color: 'red',
      });
      return;
    }
    setDeleteTaskTarget(task);
  };
  document.addEventListener('click', handler, true);
  return () => document.removeEventListener('click', handler, true);
}, [
  store,
  openAddTaskModal,
  openTaskDetailsPanel,
  canTaskCreate,
  canTaskDelete,
  selectVisibleLeavesForAssign,
  deselectVisibleLeavesForAssign,
  toggleAssignTaskSelected,
  openAssignUsersDrawer,
  assignMockEnabled,
  effectiveLocationIdByTaskId,
  assignLocationOptions,
  assignTaskCatalog,
]);

/** Clear gantt row selection when the details panel closes. */
const handleTaskDetailsClose = useCallback(() => {
  const gantt = ganttRef.current?.instance as {
    unselectTask?: (id: string | number) => void;
    getSelectedId?: () => string | number | null;
  } | undefined;
  const selectedId = gantt?.getSelectedId?.();
  if (gantt?.unselectTask && selectedId != null && selectedId !== '') {
    gantt.unselectTask(selectedId);
  }
}, []);

/**
 * Double-click Task name to rename inline. Always return false to keep DHTMLX lightbox blocked.
 */
useEffect(() => {
  let handlerId: number | string | null = null;
  const timer = window.setTimeout(() => {
    const gantt = ganttRef.current?.instance as {
      attachEvent?: (
        name: string,
        handler: (id: string | number, e?: Event) => boolean
      ) => number | string;
      detachEvent?: (id: number | string) => void;
      ext?: {
        inlineEditors?: {
          locateCell?: (target: EventTarget | null) => { id: string | number; columnName: string } | null;
          startEdit?: (id: string | number, columnName: string) => void;
        };
      };
      _is_icon_open_click?: (e: Event) => boolean;
    } | undefined;
    if (!gantt?.attachEvent) return;

    handlerId = gantt.attachEvent('onTaskDblClick', (id, e) => {
      const target = (e?.target ?? null) as HTMLElement | null;
      if (
        target?.closest?.(
          '.gantt-task-add-btn, .gantt-task-delete-btn, .gantt-task-edit-btn, .gantt-task-issue-count, .gantt-task-row-actions, .gantt-srno-edit-btn',
        )
      ) {
        return false;
      }
      if (e && gantt._is_icon_open_click?.(e)) {
        return false;
      }

      const inlineEditors = gantt.ext?.inlineEditors;
      const located = e?.target ? inlineEditors?.locateCell?.(e.target) : null;
      const inNameCell = Boolean(
        target?.closest?.('.gantt-task-name-cell, .gantt-task-name-text') ||
          located?.columnName === 'text',
      );
      if (inNameCell && inlineEditors?.startEdit) {
        taskNameInlineEditAllowedRef.current = true;
        try {
          inlineEditors.startEdit(id, 'text');
        } finally {
          // Keep flag true through sync onBeforeEditStart, then clear.
          Promise.resolve().then(() => {
            taskNameInlineEditAllowedRef.current = false;
          });
        }
      }
      return false;
    });
  }, 400);

  return () => {
    window.clearTimeout(timer);
    const gantt = ganttRef.current?.instance as {
      detachEvent?: (id: number | string) => void;
    } | undefined;
    if (handlerId != null && gantt?.detachEvent) {
      gantt.detachEvent(handlerId);
    }
  };
}, []);

/**
 * Handle link add (when user edits predecessor via built-in editor)
 */
const handleLinkAdd = useCallback(
  (id: string | number, link: any) => {
    if (predecessorLinkCommitInProgressRef.current) return;
    try {
      const linkId = Number(id);
      const existing = store.getLinks().find((l) => l.id === linkId);
      if (existing) {
        console.warn(`[ZustandGantt] Link ${linkId} already exists, skipping add`);
        return;
      }
      store.addLink({
        id: linkId,
        source: String(link.source),
        target: String(link.target),
        type: String(link.type ?? '0'),
        lag: link.lag ?? null,
      });
      commitPredecessorLinkChanges(
        String(link.target),
        {
          addedLinkIds: [linkId],
          removedLinkIds: [],
          affectedTaskIds: [String(link.source), String(link.target)],
          addedLinks: [{
            id: linkId,
            source: String(link.source),
            target: String(link.target),
            type: String(link.type ?? '0'),
            lag: link.lag ?? null,
          }],
          removedLinks: [],
        },
        { ganttLinksAlreadySynced: true },
      );
    } catch (err) {
      console.error('[ZustandGantt] Error adding link:', err);
    }
  },
  [store, commitPredecessorLinkChanges],
);

/**
 * Handle link delete (when user removes predecessor via built-in editor)
 */
const handleLinkDelete = useCallback(
  (id: string | number) => {
    if (predecessorLinkCommitInProgressRef.current) return;
    try {
      const linkId = Number(id);
      const existing = store.getLinks().find((l) => l.id === linkId);
      store.removeLink(linkId);
      commitPredecessorLinkChanges(
        existing ? String(existing.target) : '',
        {
          addedLinkIds: [],
          removedLinkIds: [linkId],
          affectedTaskIds: existing
            ? [String(existing.source), String(existing.target)]
            : [],
          addedLinks: [],
          removedLinks: existing
            ? [{
                id: linkId,
                source: String(existing.source),
                target: String(existing.target),
              }]
            : [],
        },
        { ganttLinksAlreadySynced: true },
      );
    } catch (err) {
      console.error('[ZustandGantt] Error removing link:', err);
    }
  },
  [store, commitPredecessorLinkChanges],
);

/**
 * Set up auto-scheduling event listener to capture cascading changes.
 * Only merge schedule fields for affected tasks — never replace the full store from serialize()
 * (that can drop tasks dhtmlx omits and blank the React Gantt chart).
 * Wall-clock equality gates false dirty flags from date format / calendar snap noise.
 */
const handleBeforeAutoSchedule = useCallback(() => {
  autoScheduleApplyingRef.current = true;
  if (autoScheduleMuteSafetyTimerRef.current != null) {
    window.clearTimeout(autoScheduleMuteSafetyTimerRef.current);
  }
  // Safety: clear if onAfterAutoSchedule never fires (engine no-op / error).
  // Large cascades can exceed 15s on the main thread — keep mute until after-apply settle.
  autoScheduleMuteSafetyTimerRef.current = window.setTimeout(() => {
    autoScheduleApplyingRef.current = false;
    autoScheduleMuteSafetyTimerRef.current = null;
    accumulatedChanges.current.clear();
  }, 120_000);
  return true;
}, []);

const handleAutoSchedule = useCallback((taskId: string | number, updatedTasks: any[]) => {
  const capturedUpdated = updatedTasks ? [...updatedTasks] : [];
  const apply = () => {
  autoScheduleApplyingRef.current = true;
  try {
  if (dumpTimer.current) {
    clearTimeout(dumpTimer.current);
    dumpTimer.current = null;
  }
  accumulatedChanges.current.clear();

  const gantt = ganttRef.current?.instance as any;
  if (!gantt) {
    console.error('[handleAutoSchedule] No gantt instance available');
    return;
  }

  markGanttPerf('autoscheduleTouched');
  markGanttPerf('autoscheduleRealDirty');

  const affectedIds = new Set<string>([String(taskId)]);
  for (const item of capturedUpdated) {
    if (item == null) continue;
    if (typeof item === 'object' && 'id' in item && item.id != null) {
      affectedIds.add(String(item.id));
    } else {
      affectedIds.add(String(item));
    }
  }

  const batchUpdates = new Map<string, Partial<TaskSnapshot>>();
  for (const id of affectedIds) {
    if (!gantt.isTaskExists?.(id)) continue;
    const ganttTask = gantt.getTask(id);
    const existing = store.getTask(id);
    if (!ganttTask || !existing) continue;

    const patch = buildSchedulePatchFromGanttTask(ganttTask, existing, formatDate);
    if (patch) {
      batchUpdates.set(id, patch);
    }
  }

  // While panel-driven predecessor replace is mid-flight, store already has the correct
  // post-edit links. Merging from gantt here (often after delete, before add) can desync
  // ids and blank the Predecessor cell.
  let linksChanged = false;
  if (!predecessorLinkCommitInProgressRef.current) {
    const allGanttLinks = gantt.getLinks();
    const storeLinks = store.getLinks();
    if (!linkGraphsEquivalent(storeLinks, allGanttLinks)) {
      const linkSnapshots: LinkSnapshot[] = allGanttLinks.map((l: any) => {
        const rawId = l.id;
        const numericId = Number(rawId);
        const id =
          Number.isFinite(numericId) && String(numericId) === String(rawId).trim()
            ? numericId
            : rawId;
        return {
          id,
          source: String(l.source),
          target: String(l.target),
          type: String(l.type ?? '0'),
          lag: l.lag ?? null,
        };
      });
      store.setLinks(mergeLinkSnapshotsFromGantt(storeLinks, linkSnapshots));
      linksChanged = true;
    }
  }

  // Mark Save dirty without mass refreshTask (pending-highlight effect would freeze ~16k).
  skipPendingHighlightRefreshRef.current = true;
  if (batchUpdates.size > 0) {
    store.batchUpdateTasks(batchUpdates);
    store.addAffectedTasks([...batchUpdates.keys()]);
    const seedId = String(taskId);
    const seedPatch = batchUpdates.get(seedId);
    if (seedPatch) {
      store.addEditedTasks([seedId], columnKeysFromTaskPatch(seedPatch));
    }
  }

  scheduleBackgroundPersist(
    'auto-schedule',
    batchUpdates.size > 0 ? [...batchUpdates.keys()] : [],
  );

  measureGanttPerf('autoscheduleTouched');
  measureGanttPerf('autoscheduleRealDirty');
  logAutoscheduleDirtyRatio(affectedIds.size, batchUpdates.size);

  // Chart already shows the cascade. Never mass-refreshTask / full-render here.
  if (linksChanged) {
    repopulateGanttLinkPointers(gantt);
    lastLinkPointersRevisionRef.current = store.getLinksRevision();
  }
  } finally {
    // Keep mute briefly so late onAfterTaskUpdate / React sync cannot refill a
    // multi-thousand dump and freeze the tab on "Updating schedule".
    if (autoScheduleMuteSafetyTimerRef.current != null) {
      window.clearTimeout(autoScheduleMuteSafetyTimerRef.current);
    }
    autoScheduleMuteSafetyTimerRef.current = window.setTimeout(() => {
      autoScheduleApplyingRef.current = false;
      autoScheduleMuteSafetyTimerRef.current = null;
      accumulatedChanges.current.clear();
      if (dumpTimer.current) {
        clearTimeout(dumpTimer.current);
        dumpTimer.current = null;
      }
    }, 2500);
  }
};

  // Sync apply — no "Updating schedule" overlay. Bookkeeping only; UI already updated.
  apply();
  if (showCriticalPathOnly || highlightCriticalPath) {
    setCriticalFilterEpoch((n: number) => n + 1);
  }
}, [store, formatDate, scheduleBackgroundPersist, showCriticalPathOnly, highlightCriticalPath]);


const resolvePredecessorWbsCode = useCallback((taskId: string) => {
  const snap = store.getTask(taskId);
  const srNo = snap?.srNo != null ? String(snap.srNo).trim() : '';
  if (srNo) return srNo;

  const gantt = ganttRef.current?.instance as {
    isTaskExists?: (id: string) => boolean;
    getTask?: (id: string) => { id: string | number };
    getWBSCode?: (task: { id: string | number }) => string;
  } | undefined;
  try {
    if (!gantt?.isTaskExists?.(taskId)) return null;
    const row = gantt.getTask?.(taskId);
    return row ? gantt.getWBSCode?.(row) ?? null : null;
  } catch {
    return null;
  }
}, [store]);
resolvePredecessorWbsCodeRef.current = resolvePredecessorWbsCode;

/**
 * DHTMLX predecessor editor resolves tokens via getTaskByWBSCode (tree WBS).
 * Prefer API Sr No so grid Predecessor cells accept the same values as the Add Task modal.
 */
useEffect(() => {
  if (!calendarReady) return;
  const gantt = ganttRef.current?.instance as {
    getTaskByWBSCode?: (code: string) => { id: string | number } | null;
    isTaskExists?: (id: string | number) => boolean;
    getTask?: (id: string | number) => { id: string | number };
    __srNoPredecessorLookupPatched?: boolean;
  } | undefined;
  if (!gantt?.getTaskByWBSCode || gantt.__srNoPredecessorLookupPatched) return;

  const originalGetTaskByWBSCode = gantt.getTaskByWBSCode.bind(gantt);
  gantt.getTaskByWBSCode = (code: string) => {
    const key = String(code ?? '').trim();
    if (key) {
      for (const task of store.getTasks()) {
        const srNo = task.srNo != null ? String(task.srNo).trim() : '';
        if (!srNo) continue;
        if (srNo === key || srNo.toLowerCase() === key.toLowerCase()) {
          if (gantt.isTaskExists?.(task.id)) {
            return gantt.getTask?.(task.id) ?? null;
          }
        }
      }
    }
    return originalGetTaskByWBSCode(code);
  };
  gantt.__srNoPredecessorLookupPatched = true;
}, [calendarReady, store]);




/**
 * Apply task details Save to dhtmlx + session tracking (store is updated by the draft hook before this runs).
 */
const handleTaskPanelUpdate = useCallback((taskId: string, changes: Partial<TaskSnapshot>) => {
  if (!taskId || !changes || Object.keys(changes).length === 0) return;

  // Keep dhtmlx task model in sync when user confirms Save in task details drawer/panel.
  const gantt = ganttRef.current?.instance as any;
  let shouldRunAutoSchedule = false;
  if (gantt?.isTaskExists?.(taskId)) {
    const ganttTask = gantt.getTask(taskId);
    if (ganttTask) {
      if (changes.taskName !== undefined) {
        ganttTask.text = changes.taskName ?? '';
      }
      const snap = store.getTask(taskId);
      if (
        changes.start !== undefined ||
        changes.finish !== undefined ||
        changes.duration !== undefined
      ) {
        const schedule = buildDhtmlxScheduleFields({
          start: snap?.start ?? changes.start,
          finish: snap?.finish ?? changes.finish,
          duration: snap?.duration ?? changes.duration,
          milestone: snap?.milestone,
        });
        if (schedule.start_date) ganttTask.start_date = schedule.start_date;
        if (schedule.end_date) ganttTask.end_date = schedule.end_date;
        if (schedule.duration != null) ganttTask.duration = schedule.duration;
        shouldRunAutoSchedule = true;
      }
      if (changes.percentComplete !== undefined && changes.percentComplete != null) {
        const pct = Number(changes.percentComplete);
        ganttTask.progress = Number.isFinite(pct) ? Math.max(0, Math.min(1, pct / 100)) : ganttTask.progress;
      }
      if (changes.taskType !== undefined) {
        ganttTask.taskType = changes.taskType ?? null;
      }
      if (changes.locationType !== undefined) {
        ganttTask.locationType = changes.locationType ?? null;
      }
      if (changes.locationId !== undefined) {
        ganttTask.locationId = changes.locationId ?? null;
      }
      gantt.updateTask(taskId);
      if (shouldRunAutoSchedule && gantt.config?.auto_scheduling && gantt.autoSchedule) {
        gantt.autoSchedule(taskId);
      }
      // Draft already wrote inclusive start/finish/duration to the store. Gantt dates
      // use the MSP inclusive↔exclusive bridge so Live Schedule keeps a consistent triple.
    }
  }

  store.addEditedTasks([taskId], columnKeysFromTaskPatch(changes));
}, [store]);

const handleConvertTaskToWbs = useCallback(
  async (taskId: string) => {
    if (!projectId) {
      notifications.show({ title: 'Error', message: 'No project ID', color: 'red' });
      return;
    }
    const task = store.getTask(taskId);
    const total = task?.totalQuantity;
    if (task && total != null && total > 0 && effectiveUom(taskId, (id) => store.getTask(id)).isDisplayFallback) {
      notifications.show({
        title: 'UOM required',
        message: 'Set a unit of measure on this task or a parent before tracking it.',
        color: 'red',
      });
      return;
    }
    setTrackTaskLoadingTaskId(taskId);
    try {
      // convertTaskToWBS must complete (HTTP response) before fetchTask runs.
      const res = await convertTaskToWBS({ projectId, taskId });
      try {
        const raw = await fetchTask({ projectId, taskId });
        const row = unwrapPlanningFetchTaskResponse(raw, taskId);
        if (!row || planningFetchTaskRowId(row) !== String(taskId)) {
          throw new Error('fetchTask: missing or mismatched task in response');
        }
        const refreshed = mapPlanningApiTaskRowToSnapshot(row);
        // Single-task fetch does not compute sr_no. That number comes from the project list.
        const fetchedSrNo = row.srNo ?? row.sr_no;
        const existingSrNo = store.getTask(taskId)?.srNo;
        if ((fetchedSrNo == null || String(fetchedSrNo).trim() === '') && existingSrNo) {
          refreshed.srNo = existingSrNo;
        }
        store.updateTask(taskId, refreshed);
        await store.persist(projectId);
      } catch (refreshErr) {
        console.error('[ZustandGantt] Single-task refetch after convertTaskToWBS failed:', refreshErr);
        notifications.show({
          title: 'Partial success',
          message:
            'Task was tracked on the server, but refreshing local data failed. Use Invalidate & Fetch to reload the cache.',
          color: 'orange',
        });
        return;
      }
      notifications.show({
        title: 'Success',
        message: res.message || 'Task submitted for WBS conversion.',
        color: 'teal',
      });
    } catch {
      // Error toast is shown by convertTaskToWBS
    } finally {
      setTrackTaskLoadingTaskId(null);
    }
  },
  [projectId, store]
);






const handleExportProjectFile = useCallback(async () => {
  if (!projectId) {
    notifications.show({
      title: 'Export unavailable',
      message: 'No project is selected.',
      color: 'red',
    });
    return;
  }
  setExporting(true);
  setExportProgress(0);
  try {
    await exportProjectFile({
      projectId,
      onProgress: setExportProgress,
    });
    setExportProgress(100);
    await new Promise((resolve) => window.setTimeout(resolve, 900));
  } catch {
    // Error notification shown by API helper.
  } finally {
    setExporting(false);
    setExportProgress(0);
  }
}, [projectId]);

const handleExportLookAheadExcel = useCallback(async () => {
  if (!projectId) {
    notifications.show({
      title: 'Export unavailable',
      message: 'No project is selected.',
      color: 'red',
    });
    return;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  const ymd = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const exportEnd = capLookAheadExportEndDate(targetsWindow.end, targetsWindow.maxDaysFromToday);
  const lookAheadParams = {
    projectId,
    startWindow: ymd(targetsWindow.start),
    endWindow: ymd(exportEnd),
    matchStart: true,
    matchEnd: true,
  };
  setExportingExcel(true);
  try {
    await exportLookAheadExcel(lookAheadParams);
  } catch {
    // Error notification shown by API helper.
  } finally {
    setExportingExcel(false);
  }
}, [projectId, targetsWindow.start, targetsWindow.end, targetsWindow.maxDaysFromToday]);

const handleExportLookAheadPdf = useCallback(async () => {
  if (!projectId) {
    notifications.show({
      title: 'Export unavailable',
      message: 'No project is selected.',
      color: 'red',
    });
    return;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  const ymd = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const exportEnd = capLookAheadExportEndDate(targetsWindow.end, targetsWindow.maxDaysFromToday);
  setExportingExcel(true);
  try {
    await exportLookAheadPdf({
      projectId,
      startWindow: ymd(targetsWindow.start),
      endWindow: ymd(exportEnd),
      matchStart: true,
      matchEnd: true,
    });
  } catch {
    // Error notification shown by API helper.
  } finally {
    setExportingExcel(false);
  }
}, [projectId, targetsWindow.start, targetsWindow.end, targetsWindow.maxDaysFromToday]);

const handleExportScheduleExcel = useCallback(async () => {
  if (!projectId) {
    notifications.show({
      title: 'Export unavailable',
      message: 'No project is selected.',
      color: 'red',
    });
    return;
  }
  setExportingExcel(true);
  try {
    await exportScheduleExcel({ projectId });
  } catch {
    // Error notification shown by API helper.
  } finally {
    setExportingExcel(false);
  }
}, [projectId]);

const toolsDataItems = useMemo<GanttToolsActionItem[]>(
  () => [
    {
      id: 'gantt-assign-users',
      label: assignMockEnabled ? 'Assign users (preview)' : 'Assign users',
      description: assignMockEnabled
        ? 'Open the Assign users drawer with mock data (no API). Remove ?assignMock=1 for live mode.'
        : 'Assign planners, capturers, or reviewers to locations or tasks.',
      Icon: IconUserPlus,
      onActivate: () => {
        openAssignUsersDrawer({
          mode: assignMockEnabled ? 'mock' : 'live',
        });
      },
    },
    {
      id: 'gantt-reassign-locations',
      label: reassignLocationsLoading ? 'Reassign locations…' : 'Reassign locations',
      description:
        'Remap tasks to locations using the saved location hierarchy for this project. Use after location structure changes.',
      Icon: IconMapPin,
      disabled: reassignLocationsLoading || saving || discarding,
      onActivate: () => {
        if (reassignLocationsLoading || saving || discarding) return;
        requestReassignLocations();
      },
    },
    {
      id: 'gantt-bulk-unknown-task-types',
      label: bulkUnknownTypeLoading
        ? 'Fix unknown task types…'
        : 'Fix unknown task types',
      description:
        'Set unknown leaves to Construction Activity and unknown summaries to Location when a location id is available.',
      Icon: IconTags,
      disabled:
        bulkUnknownTypeLoading || saving || discarding || reassignLocationsLoading,
      onActivate: () => {
        if (
          bulkUnknownTypeLoading ||
          saving ||
          discarding ||
          reassignLocationsLoading
        ) {
          return;
        }
        requestBulkUnknownTaskTypes();
      },
    },
    {
      id: 'gantt-invalidate-fetch',
      label: 'Invalidate & Fetch',
      description:
        "Clears this project's local cache and reloads the latest schedule from the server. Use when the chart looks out of date.",
      Icon: IconRefresh,
      onActivate: () => {
        void handleInvalidateAndFetch();
      },
    },
  ],
  [
    handleInvalidateAndFetch,
    requestReassignLocations,
    requestBulkUnknownTaskTypes,
    reassignLocationsLoading,
    bulkUnknownTypeLoading,
    saving,
    discarding,
    assignMockEnabled,
    openAssignUsersDrawer,
  ],
);

const toolsExportItems = useMemo<GanttToolsActionItem[]>(() => {
  const exportBusy =
    !projectId || saving || discarding || exporting || exportingExcel || baselineSubmitting;
  const lookAheadWindowSuffix = lookAheadExportWindowCaption
    ? ` · ${lookAheadExportWindowCaption}`
    : '';
  const excelDescription =
    viewMode === 'targets'
      ? `Look-ahead schedule as Excel${lookAheadWindowSuffix}`
      : 'Download schedule as Excel';
  const pdfDescription = `Look-ahead schedule as PDF${lookAheadWindowSuffix}`;

  const items: GanttToolsActionItem[] = [
    {
      id: 'gantt-export-xml',
      label: exporting ? 'Downloading XML…' : 'Download XML',
      description: 'An MS Project compatible XML',
      Icon: IconFileTypeXml,
      disabled: exportBusy,
      onActivate: () => {
        if (exportBusy) return;
        void handleExportProjectFile();
      },
    },
    {
      id: 'gantt-export-excel',
      label: exportingExcel ? 'Downloading Excel…' : 'Download Excel',
      description: excelDescription,
      Icon: IconFileSpreadsheet,
      disabled: exportBusy,
      onActivate: () => {
        if (exportBusy) return;
        if (viewMode === 'targets') void handleExportLookAheadExcel();
        else void handleExportScheduleExcel();
      },
    },
  ];
  if (viewMode === 'targets') {
    items.push({
      id: 'gantt-export-pdf',
      label: 'Download PDF',
      description: pdfDescription,
      Icon: IconFileTypePdf,
      disabled: exportBusy,
      onActivate: () => {
        if (exportBusy) return;
        void handleExportLookAheadPdf();
      },
    });
  }
  return items;
}, [
  projectId,
  saving,
  discarding,
  exporting,
  exportingExcel,
  baselineSubmitting,
  viewMode,
  lookAheadExportWindowCaption,
  handleExportProjectFile,
  handleExportLookAheadExcel,
  handleExportScheduleExcel,
  handleExportLookAheadPdf,
]);

const busyOverlay = resolveGanttBusyOverlayState({
  saving,
  discarding,
  storeLoading,
  loadingProgress,
  projectDisplayName,
  hasScheduleData: dhtmlxTasks.length > 0,
  localProcessing,
  recalculating: manualRecalculateLoading || scheduleRecalcLoading,
});
const viewSwitchOverlayVisible = viewSwitchBusy && !busyOverlay.visible;

// Get session metadata for display
const totalSessionChanges = sessionMetadata.taskCount + sessionMetadata.linkCount;
const totalTaskCount = scheduleTasksForReadiness.length;

// Current task's location (used to pre-select node in Location Hierarchy UI)
const currentTask = locationModalTaskId ? store.getTask(locationModalTaskId) : null;
const currentTaskLocationId = currentTask?.locationId ?? null;
const currentTaskLocationName = currentTask?.locationType ?? null;

  return {
    compareBaselinesActive,
    tasks,
    targetsVisibleRows,
    lookAheadSnapshots,
    targetsData,
    ganttTasksForParseRef,
    ganttLinksForParseRef,
    ganttColumns,
    config,
    templates,
    prevPendingHighlightRef,
    handleManualRecalculate,
    leaveBlocker,
    scheduleTasksForReadiness,
    handleReadinessTip,
    readinessSuppressed,
    formatDate,
    applyTaskTypeChange,
    dumpAccumulatedChanges,
    handleBeforeTaskDrag,
    handleAfterTaskDrag,
    handleTaskUpdate,
    openTaskDetailsPanel,
    handleTaskSelected,
    handleTaskDetailsClose,
    handleLinkAdd,
    handleLinkDelete,
    handleBeforeAutoSchedule,
    handleAutoSchedule,
    resolvePredecessorWbsCode,
    handleTaskPanelUpdate,
    handleConvertTaskToWbs,
    handleExportProjectFile,
    handleExportLookAheadExcel,
    handleExportLookAheadPdf,
    handleExportScheduleExcel,
    toolsDataItems,
    toolsExportItems,
    busyOverlay,
    viewSwitchOverlayVisible,
    totalSessionChanges,
    totalTaskCount,
    currentTask,
    currentTaskLocationId,
    currentTaskLocationName,
  };
}
